import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TabModel } from 'app/models/shared/tab.model';
import { WebsiteModel } from 'app/models/website-admin/website.model';
import { WebsiteDealershipModel } from 'app/models/website-admin/websiteDealership.model';
import { Constants } from 'app/utility/constants';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';
import { UserService } from './user.service';
import { ExternalProcessorControlledField, SecurityRight, VehicleClass, WebsiteProductSortType, WebsiteTestimonialSortType } from 'app/utility/enums';
import { FieldGroupModel } from 'app/models/shared/fieldGroup.model';
import { FieldModel } from 'app/models/shared/field.model';
import { ListItemModel } from 'app/models/shared/listItem.model';
import { CacheService } from './cache.service';
import { WebsiteTimezoneModel } from 'app/models/website-admin/websiteTimezone.model';
import { WebsiteContactCategoryModel, WebsiteContactModel, WebsiteSalesPersonRatingModel, WebsiteSalesPersonRatingWrapModel, WebsiteSalesPersonTypeModel } from 'app/models/website-admin/websiteContact.model';
import { WebsiteBlogEntryModel, WebsiteBlogEntryResultModel } from 'app/models/website-admin/websiteBlogEntry.model';
import { WebsiteTestimonialModel, WebsiteTestimonialResultModel } from 'app/models/website-admin/websiteTestimonial.model';
import { WebsiteProductModel } from 'app/models/website-admin/websiteProduct.model';
import { ApiService } from './api.service';
import { WebsiteVehicleModel, WebsiteVehicleSummaryModel } from 'app/models/website-admin/websiteVehicle';
import { GeneralService } from './general.service';
import { PriceEditorModel } from 'app/models/shared/priceEditor.model';
import { CustomFieldDisplayModel } from 'app/models/shared/customField.model';
import { LookupModel } from 'app/models/shared/lookup.model';
import { WebsiteVehicleSearchParamsModel } from 'app/models/website-admin/websiteVehicleSearchParams.model';
import { WebsiteVehicleResultModel, WebsiteVehicleSearchResultModel } from 'app/models/website-admin/websiteVehicleSearchResult.model';
import { ExportService } from './export.service';
import { StorageService } from './storage.service';
import { StringHelper } from 'app/utility/stringhelper';
import { AddressLookupModel } from 'app/models/dms/addressLookup.model';
import { add } from 'date-fns';
import { ExportSpecModel } from 'app/models/dms/exportSpec.model';

@Injectable({
    providedIn: 'root'
})
export class WebsiteService 
{


    // #region Constructor
    constructor(
        public httpClient: HttpClient,
        private cache: CacheService,
        private apiService: ApiService,
        private userService: UserService,
        private generalService: GeneralService,
        private exportService: ExportService,
        private storageService: StorageService
    )
    {
        this.getLookups();
    }

    protected getLookups(): void
    {
        this.generalService.getCachedTaxStatuses(false).subscribe(w =>
        {
            this.taxStatuses = w.map(v => new ListItemModel(v.taxStatusEnum.toString(), v.name.toString(), null, null, v));
        });
        this.generalService.getCachedCountries().subscribe(x => 
        {
            this.countries = x.map(y => new ListItemModel(y.countryId.toString(), y.name));
        });
    }

    // #endregion

    // #region Active Website
    public activeWebsite: WebsiteModel;
    public activeWebsiteSubject: BehaviorSubject<WebsiteModel> = new BehaviorSubject<WebsiteModel>(null);
    public setWebsite(website: WebsiteModel)
    {
        this.activeWebsite = website;
        this.activeWebsiteSubject.next(website);
    }

    public async getDefaultWebsite(): Promise<WebsiteModel | undefined>
    {
        //get the websites list
        return new Promise<WebsiteModel | undefined>((resolve, reject) =>
        {
            this.getWebsites().subscribe({
                next: (data) =>
                {
                    if (data.length == 0)
                    {
                        resolve(undefined);
                        return;
                    }
                    const website = data.shift();
                    this.setWebsite(website);
                    resolve(website);
                },
                error: (error) =>
                {
                    console.error(error);
                    reject(error);
                }
            });
        });
    }
    // #endregion

    // #region API Endpoints
    public get Controller(): string
    {
        return 'Websites/';
    }

    public getWebsites(): Observable<WebsiteModel[]>
    {
        return this.apiService.get<WebsiteModel[]>(this.Controller);
    }

    public getWebsiteVehicles(searchParams: WebsiteVehicleSearchParamsModel): Observable<WebsiteVehicleSearchResultModel>
    {
        let httpParams = new HttpParams();
        for (var p in searchParams)
        {
            var val = searchParams[p];
            httpParams = httpParams.append(p, val);
        }
        return this.apiService.get<WebsiteVehicleSearchResultModel>(this.Controller + searchParams.websiteExportSpecId + '/Vehicles', httpParams);
    }

    public toggleVehicleVisibility(websiteId: string, vehicle: WebsiteVehicleResultModel): Observable<any>
    {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('visible', !vehicle.visibleOnWebsite);

        return this.apiService.patch(this.Controller + websiteId + '/Vehicles/' + vehicle.vehicleId + '/ToggleVisibility', null, httpParams);
    }

    public getWebsiteDealerships(websiteId: string): Observable<WebsiteDealershipModel[]>
    {
        return this.apiService.get<WebsiteDealershipModel[]>(this.Controller + websiteId + '/Dealerships');
    }

    public getWebsiteDealership(websiteId: string, dealershipInternalID: string): Observable<WebsiteDealershipModel>
    {
        return this.apiService.get<WebsiteDealershipModel>(this.Controller + websiteId + '/Dealerships/' + dealershipInternalID);
    }

    public getWebsiteDealershipTemplate(websiteId: string): Observable<WebsiteDealershipModel>
    {
        return this.apiService.get<WebsiteDealershipModel>(this.Controller + websiteId + '/Dealerships/new');
    }

    public updateWebsiteDealership(websiteId: string, dealership: WebsiteDealershipModel): Observable<WebsiteDealershipModel>
    {
        return this.apiService.patch<WebsiteDealershipModel>(this.Controller + websiteId + '/Dealerships/' + dealership.dealershipInternalID, dealership);
    }

    public toggleDealershipVisibility(websiteId: string, dealershipInternalId: string, visible: boolean): Observable<any> {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('visible', !visible);
        return this.apiService.patch(this.Controller + websiteId + '/Dealerships/' + dealershipInternalId + '/ToggleVisibility', null, httpParams);
    }

    public getBuyerscoreSalesPersonRatings(websiteId: string, dealershipInternalId: string, buyerscorePublicId: string): Observable<WebsiteSalesPersonRatingWrapModel>
    {
        return this.apiService.get<WebsiteSalesPersonRatingWrapModel>(this.Controller + websiteId + '/Dealerships/' + dealershipInternalId + '/BuyerscoreSalesPersonRatings/' + buyerscorePublicId);
    }

    public toggleContactVisibility(websiteId: string, dealershipInternalId: string, contactId: string, visible: boolean): Observable<any> {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('visible', !visible);
        return this.apiService.patch(this.Controller + websiteId + '/Dealerships/' + dealershipInternalId + '/Staff/' + contactId + '/ToggleVisibility', null, httpParams);
    }

    public getWebsiteContactTemplate(websiteId: string, dealershipInternalId: string): Observable<WebsiteContactModel>
    {
        return this.apiService.get<WebsiteContactModel>(this.Controller + websiteId + '/Dealerships/' + dealershipInternalId + '/Staff/new');
    }

    public createWebsiteContact(websiteId: string, dealershipInternalId: string, contact: WebsiteContactModel): Observable<WebsiteContactModel>
    {
        return this.apiService.post<WebsiteContactModel>(this.Controller + websiteId + '/Dealerships/' + dealershipInternalId + '/Staff/new', contact);
    }

    public updateWebsiteContact(websiteId: string, dealershipInternalId: string, contact: WebsiteContactModel): Observable<WebsiteContactModel>
    {
        return this.apiService.patch<WebsiteContactModel>(this.Controller + websiteId + '/Dealerships/' + dealershipInternalId + '/Staff/' + contact.id, contact);
    }

    public getTimezones(): Observable<WebsiteTimezoneModel[]>
    {
        return this.apiService.get<WebsiteTimezoneModel[]>(this.Controller + 'Timezones');
    }

    public getCachedTimezones(): Observable<WebsiteTimezoneModel[]>
    {
        let cacheKey: string = 'websiteTimezones';
        let cacheMilliseconds: number = 1 * 60 * 60000 * 24; //24 hour
        return this.cache.get(cacheKey, this.getTimezones(), cacheMilliseconds);
    }

    public uploadStaffImage(websiteId: string, dealershipInternalId: string, contactId: string, image: Blob, fileExtension: string): Observable<any>
    {
        let url = Constants.ApiUrl + this.Controller + websiteId + '/Dealerships/' + dealershipInternalId + '/Staff/' + contactId + '/profile/image';
        const formData = new FormData();
        formData.append('image', image, 'image.' + fileExtension);
        return this.httpClient.post<any>(url, formData);
    }

    public updateDealershipOrder(websiteId: string, dealershipIds: string[]): Observable<any>
    {
        return this.apiService.patch(this.Controller + websiteId + '/Dealerships/Order', dealershipIds);
    }

    public updateContactOrder(websiteId: string, dealershipInternalId: string, contactIds: string[]): Observable<any>
    {
        return this.apiService.patch(this.Controller + websiteId + '/Dealerships/' + dealershipInternalId + '/Staff/Order', contactIds);
    }

    public getWebsiteBlogEntries(websiteId: string, pageSize: number, pageIndex: number, sortType: WebsiteTestimonialSortType, includeHidden: boolean, searchString: string): Observable<WebsiteBlogEntryResultModel>
    {
        let httpParams = new HttpParams()
        httpParams = httpParams.set('pageSize', pageSize);
        httpParams = httpParams.set('pageIndex', pageIndex);
        httpParams = httpParams.set('sortType', sortType);
        httpParams = httpParams.set('includeHidden', includeHidden);
        httpParams = httpParams.set('searchString', searchString);
        return this.apiService.get<WebsiteBlogEntryResultModel>(this.Controller + websiteId + '/BlogEntries', httpParams);
    }

    public getWebsiteBlogEntryTemplate(websiteId: string): Observable<WebsiteBlogEntryModel>
    {
        return this.apiService.get<WebsiteBlogEntryModel>(this.Controller + websiteId + '/BlogEntries/new');
    }

    public getWebsiteBlogEntry(websiteId: string, blogId: string): Observable<WebsiteBlogEntryModel>
    {
        return this.apiService.get<WebsiteBlogEntryModel>(this.Controller + websiteId + '/BlogEntries/' + blogId);
    }

    public updateWebsiteBlogEntry(websiteId: string, blogId: string, blogEntry: WebsiteBlogEntryModel): Observable<WebsiteBlogEntryModel>
    {
        return this.apiService.post<WebsiteBlogEntryModel>(this.Controller + websiteId + '/BlogEntries/' + blogId, blogEntry);
    }

    public toggleBlogEntryVisibility(websiteId: string, blogEntryId: string, visible: boolean): Observable<any> {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('visible', !visible);
        return this.apiService.patch(this.Controller + websiteId + '/BlogEntries/' + blogEntryId + '/ToggleVisibility', null, httpParams);
    }

    public uploadBlogEntryThumbnailImage(websiteId: string, blogEntryId: string, image: Blob, fileExtension: string): Observable<any>
    {
        let url = Constants.ApiUrl + this.Controller + websiteId + '/BlogEntries/' + blogEntryId + '/thumbnail';
        const formData = new FormData();
        formData.append('image', image, 'image.' + fileExtension);
        return this.httpClient.post<any>(url, formData);
    }

    public uploadBlogEntryContentImage(websiteId: string, blogEntryId: string, image: Blob): Observable<any>
    {
        let url = Constants.ApiUrl + this.Controller + websiteId + '/BlogEntries/' + blogEntryId + '/image';
        const formData = new FormData();
        formData.append('image', image);
        return this.httpClient.post<any>(url, formData);
    }

    public getWebsiteTestimonials(websiteId: string, pageSize: number, pageIndex: number, sortType: WebsiteTestimonialSortType, includeHidden: boolean, searchString: string): Observable<any>
    {
        let httpParams = new HttpParams()
        httpParams = httpParams.set('pageSize', pageSize);
        httpParams = httpParams.set('pageIndex', pageIndex);
        httpParams = httpParams.set('sortType', sortType);
        httpParams = httpParams.set('includeHidden', includeHidden);
        httpParams = httpParams.set('searchString', searchString);
        return this.apiService.get<any>(this.Controller + websiteId + '/Testimonials', httpParams);
    }

    public getWebsiteTestimonialTemplate(websiteId: string): Observable<any>
    {
        return this.apiService.get<any>(this.Controller + websiteId + '/Testimonials/new');
    }

    public getWebsiteTestimonial(websiteId: string, testimonialId: string): Observable<WebsiteTestimonialModel>
    {
        return this.apiService.get<WebsiteTestimonialModel>(this.Controller + websiteId + '/Testimonials/' + testimonialId);
    }

    public uploadTestimonialThumbnailImage(websiteId: string, testimonialId: string, image: Blob, fileExtension: string): Observable<any>
    {
        let url = Constants.ApiUrl + this.Controller + websiteId + '/Testimonials/' + testimonialId + '/image';
        const formData = new FormData();
        formData.append('image', image, 'image.' + fileExtension);
        return this.httpClient.post<any>(url, formData);
    }

    public updateWebsiteTestimonial(websiteId: string, id: string, testimonial: WebsiteTestimonialModel): Observable<any>
    {
        return this.apiService.post<any>(this.Controller + websiteId + '/Testimonials/' + id, testimonial);
    }

    public toggleTestimonialVisibility(websiteId: string, testimonialId: string, visible: boolean): Observable<any> {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('visible', !visible);
        return this.apiService.patch(this.Controller + websiteId + '/Testimonials/' + testimonialId + '/ToggleVisibility', null, httpParams);
    }

    public getWebsiteVehicleById(websiteId: string, vehicleId: string): Observable<WebsiteVehicleModel>
    {

        return this.apiService.get<WebsiteVehicleModel>(this.Controller + websiteId + '/Vehicles/' + vehicleId);
    }

    public toggleProductVisibility(websiteId: string, productId: string, visible: boolean): Observable<any> {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('visible', !visible);
        return this.apiService.patch(this.Controller + websiteId + '/Products/' + productId + '/ToggleVisibility', null, httpParams);
    }

    public getWebsiteProducts(websiteId: string, pageSize: number, pageIndex: number, sortType: WebsiteProductSortType, includeHidden: boolean, searchString: string): Observable<any>
    {
        let httpParams = new HttpParams()
        httpParams = httpParams.set('pageSize', pageSize);
        httpParams = httpParams.set('pageIndex', pageIndex);
        httpParams = httpParams.set('sortType', sortType);
        httpParams = httpParams.set('includeHidden', includeHidden);
        httpParams = httpParams.set('searchString', searchString);
        return this.apiService.get<any>(this.Controller + websiteId + '/Products', httpParams);
    }

    public getWebsiteProductTemplate(websiteId: string): Observable<any>
    {
        return this.apiService.get<any>(this.Controller + websiteId + '/Products/new');
    }

    public getWebsiteProduct(websiteId: string, productId: string): Observable<WebsiteProductModel>
    {
        return this.apiService.get<any>(this.Controller + websiteId + '/Products/' + productId);
    }

    public uploadProductThumbnailImages(websiteId: string, productId: string, images: Blob[]): Observable<any>
    {
        const url = Constants.ApiUrl + this.Controller + websiteId + '/Products/' + productId + '/image/new';
        const formData = new FormData();
        for (var i = 0; i < images.length; i++)
        {
            formData.append('images', images[i]);
        }
        return this.httpClient.post<any>(url, formData, { reportProgress: true, observe: 'events' });

    }

    public updateWebsiteProduct(websiteId: string, id: string, product: any): Observable<any>
    {
        return this.apiService.post<any>(this.Controller + websiteId + '/Products/' + id, product);
    }
    // #endregion

    // #region Fieldset

    // #region Dealership
    public async setupDealershipFieldGroups(dealership: WebsiteDealershipModel, editActive: boolean = false, makeDirty: boolean = false): Promise<TabModel[]>
    {

        this.editActive = editActive;

        var fieldGroupTabs: TabModel[] = [];

        if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
        {
            fieldGroupTabs.push(new TabModel("Details", dealership, await this.getDealershipFieldGroupsAsync(dealership, "Details"), makeDirty, "details", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        }

        if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
        {
            fieldGroupTabs.push(new TabModel("Hours", dealership.businessHours, await this.getDealershipFieldGroupsAsync(dealership, "Hours"), makeDirty, "hours", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        }

        if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
        {
            fieldGroupTabs.push(new TabModel("Staff", dealership, await this.getDealershipFieldGroupsAsync(dealership, "Contacts"), makeDirty, "contacts", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        }

        if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
        {
            fieldGroupTabs.push(new TabModel("Enquiries", dealership, await this.getDealershipFieldGroupsAsync(dealership, "Enquiries"), makeDirty, "enquiries", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        }

        if (this.userService.hasAllRights([SecurityRight.AdminWebsiteMan, SecurityRight.SupportUser]))
        {
            fieldGroupTabs.push(new TabModel("MC Admin", dealership, await this.getDealershipFieldGroupsAsync(dealership, "MC Admin"), makeDirty, "mc admin", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));

        }

        // console.log("fieldGroupTabs", fieldGroupTabs);
        return fieldGroupTabs;
    }

    timezones: ListItemModel[];
    private async getDealershipFieldGroupsAsync(dealership: WebsiteDealershipModel, groupId: string): Promise<FieldGroupModel[]>
    {
        try
        {
            const timezones = await lastValueFrom(this.getCachedTimezones());
            this.timezones = timezones.map(v => new ListItemModel(v.timeZoneId, v.displayName, null, null, v));
        } catch (err)
        {
            console.error('Error fetching timezones:', err);
        }

        var field_groups = [];
        var group: FieldGroupModel;
        switch (groupId)
        {
            case "Details":
                group = new FieldGroupModel("Name");
                group.addField(new FieldModel("Dealership name", dealership.name, "text", "name", this.editActive, true));
                group.addField(new FieldModel("Display on website", dealership.visible, "toggle", "visible", this.editActive, false));
                field_groups.push(group);

                group = new FieldGroupModel("Address");
                var address1 = new FieldModel("Address 1", dealership.address1, "autocomplete", "address1", this.editActive, false);
                address1.hasChildren = true;
                address1.detailClass = "address";
                address1.parent = "name";
                group.addField(address1);
                var address2 = new FieldModel("Address 2", dealership.address2, "text", "address2", this.editActive, false);
                address2.parent = "address1";
                group.addField(address2);
                var addressCity = new FieldModel("City", dealership.addressCity, "text", "addressCity", this.editActive, false);
                addressCity.parent = "address1";
                group.addField(addressCity);
                var addressGoogle = new FieldModel("Google Maps address", dealership.addressGoogle, "text", "addressGoogle", this.editActive, false);
                addressGoogle.parent = "address1";
                group.addField(addressGoogle);
                var addressLatitude = new FieldModel("Latitude", dealership.addressLatitude, "text", "addressLatitude", this.editActive, false);
                addressLatitude.parent = "address1";
                group.addField(addressLatitude);
                var addressLongitude = new FieldModel("Longitude", dealership.addressLongitude, "text", "addressLongitude", this.editActive, false);
                addressLongitude.parent = "address1";
                group.addField(addressLongitude);
                
                field_groups.push(group);

                group = new FieldGroupModel("Contact");
                group.addField(new FieldModel("Primary phone", dealership.phone, "text", "phone", this.editActive, true));
                group.addField(new FieldModel("Primary email", dealership.email, "text", "email", this.editActive, true));
                field_groups.push(group);

                group = new FieldGroupModel("Primary dealership");
                var primary = new FieldModel("Is default dealership", dealership.isDefaultDealership, "toggle", "isDefaultDealership", (this.editActive && !dealership.isDefaultDealership), false, null, null, null, true);
                primary.assistiveText = "Receives enquiries when no specific vehicle is chosen";
                group.addField(primary);
                field_groups.push(group);
                break;
            case "Hours":

                const openingTimes = this.getPossibleBusinessHours(true);
                const closingTimes = this.getPossibleBusinessHours();

                const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
                for (const day of daysOfWeek) {
                    const dayKey = day.toLowerCase();
                    const dayGroup = new FieldGroupModel(day);
                    if (!this.editActive && dealership.businessHours[`${dayKey}Open`] === "Closed") {
                        dayGroup.addField(new FieldModel("Hours", dealership.businessHours[`${dayKey}Open`], "text", `${dayKey}Open`, this.editActive, false));
                    } else {
                        const dayOpenField = new FieldModel("Opens", dealership.businessHours[`${dayKey}Open`], "select", `${dayKey}Open`, this.editActive, true, openingTimes);
                        dayOpenField.hasChildren = true;
                        dayGroup.addField(dayOpenField);

                        const dayCloseField = new FieldModel("Closes", dealership.businessHours[`${dayKey}Close`], "select", `${dayKey}Close`, this.editActive, true, closingTimes);
                        dayCloseField.parent = `${dayKey}Open`;
                        dayCloseField.toggleEditableWhen = "Closed|By Appointment";
                        dayCloseField.inverseCheck = true;
                        if (dealership.businessHours[`${dayKey}Open`] === "Closed" || dealership.businessHours[`${dayKey}Open`] === "By Appointment") {
                            dayCloseField.editable = false;
                        }
                        dayGroup.addField(dayCloseField);

                        if (day === "Monday" && this.editActive)
                        {
                            const applyAllField = new FieldModel("", "", "button", "helper", true, false, null, "no-border");
                            applyAllField.hasButton = true;
                            applyAllField.buttonText = "Apply all";
                            applyAllField.buttonName = "button-apply-all";
                            dayGroup.addField(applyAllField);
                        }
                    }
                    field_groups.push(dayGroup);
                }

                break;
            case "Contacts":
                group = new FieldGroupModel("Display order");
                var contactOrderingOptions = [
                    { id: "Ordered", name: "Same as table" },
                    { id: "Random", name: "Random" }
                ];
                group.addField(new FieldModel("Contact ordering", dealership.contactOrdering, "select", "contactOrdering", this.editActive, true, contactOrderingOptions));
                field_groups.push(group);

                group = new FieldGroupModel("Staff");
                // dealership.contacts.forEach(contact =>
                // {
                //     group.addField(new FieldModel(contact.name, contact.email, "text", "email", true, true));
                // });
                field_groups.push(group);
                break;
            case "Enquiries":
                group = new FieldGroupModel("Emails adresses");

                var helperText = new FieldModel("", "", "longtext", "helper", false, false, null, null, null, true);
                helperText.value = "Please enter the email address where you'd like to receive each type of enquiry from your website. If you’d like enquiries sent to multiple addresses, separate them with a comma. If left empty, enquiries will be sent to your dealership’s primary email address by default.";
                group.addField(helperText);

                group.addField(new FieldModel("Ask a question", dealership.emailAskQuestion, "text", "emailAskQuestion", this.editActive, false));
                group.addField(new FieldModel("General enquiry", dealership.emailGeneralEnquiry, "text", "emailGeneralEnquiry", this.editActive, false));
                group.addField(new FieldModel("Buy now", dealership.emailBuyNow, "text", "emailBuyNow", this.editActive, false));
                group.addField(new FieldModel("Claim offer", dealership.emailClaimOffer, "text", "emailClaimOffer", this.editActive, false));
                group.addField(new FieldModel("Test drive", dealership.emailTestDrive, "text", "emailTestDrive", this.editActive, false));
                group.addField(new FieldModel("Price my trade", dealership.emailPriceMyTrade, "text", "emailPriceMyTrade", this.editActive, false));
                // group.addField(new FieldModel("Make an offer", dealership.emailMakeAnOffer, "text", "emailMakeAnOffer", this.editActive, false));

                group.singleColumn = true;
                field_groups.push(group);
                break;
            case "MC Admin":
                group = new FieldGroupModel("Motorcentral");
                group.addField(new FieldModel("Motorcentral short code", dealership.shortCode, "text", "shortCode", this.editActive, true));
                group.addField(new FieldModel("Dealership file ID", dealership.fileID, "text", "fileID", this.editActive, true));
                group.addField(new FieldModel("Caption", dealership.caption, "text", "caption", this.editActive, true));
                field_groups.push(group);

                group = new FieldGroupModel("Timezone");
                group.addField(new FieldModel("Timezone", dealership.timeZoneId, "select", "timeZoneId", this.editActive, true, this.timezones));
                field_groups.push(group);

                group = new FieldGroupModel("Buyer score");
                group.addField(new FieldModel("Buyer score ID", dealership.buyerScoreId, "text", "buyerScoreId", this.editActive, false));
                field_groups.push(group);

                group = new FieldGroupModel("Recaptcha");
                group.addField(new FieldModel("Recaptcha enabled", dealership.formsRecaptchaEnabled, "toggle", "formsRecaptchaEnabled", this.editActive, false));
                field_groups.push(group);

                group = new FieldGroupModel("Auto attendant");
                group.addField(new FieldModel("Auto response email", dealership.autoResponseEmail, "text", "autoResponseEmail", this.editActive, false));
                field_groups.push(group);
                break;
        }
        return field_groups;
    }

    public applyMondayHoursToAllDays(fieldGroupTabs: TabModel[])
    {
        const hoursTab = fieldGroupTabs.find(tab => tab.id === "hours");
        if (!hoursTab) return;

        const mondayGroup = hoursTab.fieldGroups.find(group => group.id === "Monday");
        if (!mondayGroup) return;
        const mondayOpenField = mondayGroup.fields.find(field => field.id === "mondayOpen");
        const mondayCloseField = mondayGroup.fields.find(field => field.id === "mondayClose");
        if (!mondayOpenField || !mondayCloseField) return;

        const daysOfWeek = ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        for (const day of daysOfWeek) {
            const dayGroup = hoursTab.fieldGroups.find(group => group.id === day);
            if (dayGroup) {
                const dayOpenField = dayGroup.fields.find(field => field.id === `${day.toLowerCase()}Open`);
                const dayCloseField = dayGroup.fields.find(field => field.id === `${day.toLowerCase()}Close`);
                if (dayOpenField && dayCloseField) {
                    dayOpenField.value = mondayOpenField.value;
                    dayCloseField.value = mondayCloseField.value;
                    dayOpenField.dirty = true;
                    dayCloseField.dirty = true;
                    if (mondayOpenField.value === "Closed" || mondayOpenField.value === "By Appointment") {
                        dayCloseField.editable = false;
                    }
                    else {
                        dayCloseField.editable = true;
                    }
                    dayOpenField.valueChanged$.next(null);
                    dayCloseField.valueChanged$.next(null);
                }
            }
        }

        hoursTab.dirty = true;

        const tabIndex = fieldGroupTabs.findIndex(tab => tab.id === "hours");
        if (tabIndex !== -1) {
            fieldGroupTabs[tabIndex] = hoursTab;
        }

        return fieldGroupTabs;       
    }

    public getFieldGroupsForContact(contact: WebsiteContactModel, editActive: boolean, salesPersonRatingsWrap: WebsiteSalesPersonRatingWrapModel = null, contactCategories: WebsiteContactCategoryModel[] = null): FieldGroupModel[]
    {
        var field_groups = [];

        let group = new FieldGroupModel("Name");
        group.singleColumn = true;
        group.addField(new FieldModel("First Name", contact.firstName, "text", "firstName", editActive, false));
        group.addField(new FieldModel("Last Name", contact.lastName, "text", "lastName", editActive, false));
        group.addField(new FieldModel("Position", contact.position, "text", "position", editActive, false));
        if (contactCategories != null && contactCategories.length > 1)
        {
            var contactCategoriesList = [];
            contactCategories.forEach(x =>
            {
                contactCategoriesList.push({id: x.id.toString(), name: x.category});
            });
            group.addField(new FieldModel("Category", contact.contactCategoryId.toString(), "select", "contactCategoryId", editActive, false, contactCategoriesList));
            field_groups.push(group);
        }
        group = new FieldGroupModel("Contact info");
        group.singleColumn = true;
        group.addField(new FieldModel("Mobile", contact.mobile, "text", "mobile", editActive, false));
        group.addField(new FieldModel("Direct dial", contact.directDial, "text", "directDial", editActive, false));
        group.addField(new FieldModel("Email", contact.email, "text", "email", editActive, false));
        field_groups.push(group);

        group = new FieldGroupModel("Profile image");
        group.singleColumn = true;
        var profileImage = new FieldModel("Profile image", contact.photo, "photo", "photo", editActive, false);
        profileImage.allowDelete = true;
        group.addField(profileImage);
        field_groups.push(group);

        group = new FieldGroupModel("Display settings");
        group.singleColumn = true;
        group.addField(new FieldModel("Hide on vehicle page", contact.excludeFromVehicleSingle, "toggle", "excludeFromVehicleSingle", editActive, false));
        group.addField(new FieldModel("Hide on contact page", contact.excludeFromContactPage, "toggle", "excludeFromContactPage", editActive, false));
        field_groups.push(group);

        group = new FieldGroupModel("Buyerscore");
        group.singleColumn = true;
        if (salesPersonRatingsWrap != null && salesPersonRatingsWrap.salesPersonTypes && salesPersonRatingsWrap.salesPersonTypes.length > 0)
        {
            var publishRating = new FieldModel("Publish rating", contact.buyerScoreSalespersonEnabled, "toggle", "buyerScoreSalespersonEnabled", editActive, false);
            publishRating.hasChildren = true;
            group.addField(publishRating);
        }
        else
        {
            group.addField(new FieldModel(" ", "Dealership Buyerscore ID must be populated", "longtext", "buyerscore", false, false, null, null, null, true));
        }
        field_groups.push(group);

        if (salesPersonRatingsWrap != null && salesPersonRatingsWrap.salesPersonTypes && salesPersonRatingsWrap.salesPersonTypes.length > 0)
        {
            group = new FieldGroupModel("");
            group.singleColumn = true;
            group.toggleVisibilityOn = "buyerScoreSalespersonEnabled";
            group.toggleVisibleWhen = "true";
            group.hidden = !contact.buyerScoreSalespersonEnabled;

            if (contact.buyerScoreSalespersonEnabled || editActive)
            {
                var salesPersonTypesList = salesPersonRatingsWrap.salesPersonTypes.map(x => new ListItemModel(x.salesPersonType, x.salesPersonTypeDisplay, null, null, x));
                var salesPersonRatingsList = salesPersonRatingsWrap.salesPersonRatings.filter(x => x.salesPersonType == "SalesPerson").map(x => new ListItemModel(x.salesPersonName, x.salesPersonName, null, null, x));
                var businessManagerRatingsList = salesPersonRatingsWrap.salesPersonRatings.filter(x => x.salesPersonType == "BusinessManager").map(x => new ListItemModel(x.salesPersonName, x.salesPersonName, null, null, x));

                if (!editActive)
                {
                    var rating = 0;
                    if (contact.buyerScoreSalespersonName != null && contact.buyerScoreSalespersonName != "")
                    {
                        rating = salesPersonRatingsWrap.salesPersonRatings.find(x => x.salesPersonName == contact.buyerScoreSalespersonName && x.salesPersonType == contact.buyerScoreSalespersonType).rating;
                    }
                    group.addField(new FieldModel("Salesperson rating", rating, "number", "rating", false, false));
                }
                
                if (contact.buyerScoreSalespersonType == "")
                {
                    contact.buyerScoreSalespersonType = "SalesPerson";
                }
                var buyerScoreSalespersonType = new FieldModel("Rating type", contact.buyerScoreSalespersonType, "select", "buyerScoreSalespersonType", editActive, true, salesPersonTypesList);
                buyerScoreSalespersonType.hasChildren = true;
                group.addField(buyerScoreSalespersonType);

                if (editActive)
                {
                    var salesPersonRatingId = null;
                    var businessManagerRatingId = null;
                    if (salesPersonRatingsList != null && salesPersonRatingsList.length > 0)
                    {
                        salesPersonRatingId = salesPersonRatingsList[0].id;
                    }
                    if (businessManagerRatingsList != null && businessManagerRatingsList.length > 0)
                    {
                        businessManagerRatingId = businessManagerRatingsList[0].id;
                    }

                    if (contact.buyerScoreSalespersonType == "SalesPerson")
                    {
                        if (contact.buyerScoreSalespersonName == "" && salesPersonRatingId != null)
                        {   
                            contact.buyerScoreSalespersonName = salesPersonRatingId;
                        }
                        if ((!contact.buyerScoreSalespersonNameManager || contact.buyerScoreSalespersonNameManager == "") && businessManagerRatingId != null)
                        {
                            contact.buyerScoreSalespersonNameManager = businessManagerRatingId;
                        }
                    }
                    else if (contact.buyerScoreSalespersonType == "BusinessManager")
                    {
                        if (contact.buyerScoreSalespersonName == "" && salesPersonRatingId != null)
                        {   
                            contact.buyerScoreSalespersonName = salesPersonRatingId;
                        }
                        else
                        {
                            contact.buyerScoreSalespersonNameManager = contact.buyerScoreSalespersonName;
                            if (salesPersonRatingId != null)
                            {
                                contact.buyerScoreSalespersonName = salesPersonRatingId;
                            }
                        }
                        if ((!contact.buyerScoreSalespersonNameManager || contact.buyerScoreSalespersonNameManager == "") && businessManagerRatingId != null)
                        {
                            contact.buyerScoreSalespersonNameManager = businessManagerRatingId;
                        }
                    }


                    var salesPersonNameField = new FieldModel("Name match", contact.buyerScoreSalespersonName, "select", "buyerScoreSalespersonName", editActive, true, salesPersonRatingsList);
                    salesPersonNameField.parent = "buyerScoreSalespersonType";
                    salesPersonNameField.toggleEditableWhen = "SalesPerson";
                    salesPersonNameField.toggleVisibleWhen = "SalesPerson";
                    salesPersonNameField.editable = (contact.buyerScoreSalespersonType == "SalesPerson");
                    group.addField(salesPersonNameField);
                    
                    var businessManagerNameField = new FieldModel("Name match", contact.buyerScoreSalespersonNameManager, "select", "buyerScoreSalespersonNameManager", editActive, true, businessManagerRatingsList);
                    businessManagerNameField.parent = "buyerScoreSalespersonType";
                    businessManagerNameField.toggleEditableWhen = "BusinessManager";
                    businessManagerNameField.toggleVisibleWhen = "BusinessManager";
                    businessManagerNameField.editable = (contact.buyerScoreSalespersonType == "BusinessManager");
                    group.addField(businessManagerNameField);
                }
                else
                {
                    group.addField(new FieldModel("Name match", contact.buyerScoreSalespersonName, "text", "buyerScoreSalespersonName", false, false));
                }
            }

            field_groups.push(group);

        }


        return field_groups;
    }

    public syncContactFieldsWithObject(contact, modal_field_groups)
    {
        for (let field_group of modal_field_groups)
        {
            for (let field of field_group.fields)
            {
                if (contact[field.id] != null && contact[field.id] != field.value && !field.invalid && field.dirty)
                {
                    contact[field.id] = field.value;
                }
            }
        }
    }
    //#endregion

    // #region Blog
    public setupBlogEntryFieldGroups(blog: WebsiteBlogEntryModel, editActive: boolean = false, makeDirty: boolean = false): TabModel[]
    {

        this.editActive = editActive;

        var fieldGroupTabs: TabModel[] = [];

        if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
        {
            fieldGroupTabs.push(new TabModel("Details", blog, this.getBlogEntryFieldGroups(blog, "Details"), makeDirty, "details", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        }

        // console.log("fieldGroupTabs", fieldGroupTabs);
        return fieldGroupTabs;
    }

    private getBlogEntryFieldGroups(blog: WebsiteBlogEntryModel, groupId: string): FieldGroupModel[]
    {
        var field_groups = [];
        var group: FieldGroupModel;
        switch (groupId)
        {
            case "Details":
                let group = new FieldGroupModel("Details");
                group.addField(new FieldModel("Title", blog.title, "text", "title", this.editActive, false));
                group.addField(new FieldModel("Author", blog.author, "text", "author", this.editActive, false));
                group.addField(new FieldModel("Date", blog.creationTime, "date", "creationTime", this.editActive, false));
                group.addField(new FieldModel("Slug", blog.urlTitle, "text", "urlTitle", this.editActive, false));
                group.addField(new FieldModel("Meta description (SEO)", blog.metaDesc, "text", "metaDesc", this.editActive, false));
                group.singleColumn = true;
                field_groups.push(group);

                group = new FieldGroupModel("Image");
                group.addField(new FieldModel("Thumbnail", blog.thumbnailURL, "photo", "thumbnailURL", this.editActive, false));
                group.addField(new FieldModel("Thumbnail alt tag (SEO)", blog.thumbnailAltTag, "text", "thumbnailAltTag", this.editActive, false));
                group.singleColumn = true;
                field_groups.push(group);

                group = new FieldGroupModel("Blog content");
                group.addField(new FieldModel("Short description", blog.shortText, "text", "shortText", this.editActive, false));
                group.addField(new FieldModel("Article content", blog.content, "richtext", "content", this.editActive, false));
                group.singleColumn = true;
                field_groups.push(group);

                group = new FieldGroupModel("Publish to website");
                group.addField(new FieldModel("Publish", blog.enabled, "toggle", "enabled", this.editActive, false));
                group.singleColumn = true;
                field_groups.push(group);
                break;
        }
        return field_groups;
    }
    // #endregion

    // #region Testimonials
    public setupTestimonialFieldGroups(testimonial: WebsiteTestimonialModel, editActive: boolean = false, makeDirty: boolean = false): TabModel[]
    {
        this.editActive = editActive;

        var fieldGroupTabs: TabModel[] = [];

        if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
        {
            fieldGroupTabs.push(new TabModel("Details", testimonial, this.getTestimonialFieldGroups(testimonial, "Details"), makeDirty, "details", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        }

        // console.log("fieldGroupTabs", fieldGroupTabs);
        return fieldGroupTabs;
    }

    private getTestimonialFieldGroups(testimonial: WebsiteTestimonialModel, groupId: string): FieldGroupModel[]
    {
        var field_groups = [];
        var group: FieldGroupModel;
        switch (groupId)
        {
            case "Details":
                let group = new FieldGroupModel("");
                group.addField(new FieldModel("Title", testimonial.name, "text", "name", this.editActive, false));
                group.addField(new FieldModel("Date", testimonial.dateEntered, "date", "dateEntered", this.editActive, false));
                group.addField(new FieldModel("Image upload", testimonial.photo1Large, "photo", "photo1Large", this.editActive, false, testimonial.photo1Large));
                group.addField(new FieldModel("Comment", testimonial.comment, "longtext", "comment", this.editActive, false));
                group.addField(new FieldModel("Show on website", testimonial.enabled, "toggle", "enabled", this.editActive, false));
                group.singleColumn = true;
                field_groups.push(group);

                break;
        }
        return field_groups;
    }
    // #endregion

    // #region Products
    public setupProductFieldGroups(product: any, editActive: boolean = false, makeDirty: boolean = false): TabModel[]
    {
        this.editActive = editActive;

        var fieldGroupTabs: TabModel[] = [];

        if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
        {
            fieldGroupTabs.push(new TabModel("Details", product, this.getProductFieldGroups(product, "Details"), makeDirty, "details", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        }

        if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
        {
            fieldGroupTabs.push(new TabModel("Images", product, this.getProductFieldGroups(product, "Images"), makeDirty, "images", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        }

        // console.log("fieldGroupTabs", fieldGroupTabs);
        return fieldGroupTabs;
    }

    private getProductFieldGroups(product: WebsiteProductModel, groupId: string): FieldGroupModel[]
    {
        var field_groups = [];
        var group: FieldGroupModel = new FieldGroupModel("");
        switch (groupId)
        {
            case "Details":
                group = new FieldGroupModel("Details");
                group.addField(new FieldModel("Title", product.name, "text", "name", this.editActive, false));
                group.addField(new FieldModel("Price", product.price, "number", "price", this.editActive, false));
                group.addField(new FieldModel("GST Inclusive", product.isTaxInclusive, "toggle", "isTaxInclusive", this.editActive, false));
                group.addField(new FieldModel("Category", product.categoryId, "lookup", "categoryId", this.editActive, false, product.categoryEntity));
                group.singleColumn = true;
                field_groups.push(group);

                group = new FieldGroupModel("Publish to website");
                group.addField(new FieldModel("Publish", product.enabled, "toggle", "enabled", this.editActive, false));
                group.singleColumn = true;
                field_groups.push(group);

                break;
            case "Images":

                var images = product.images;

                // Map images to transient fields
                for (let i = 0; i < 20; i++)
                {
                    const image = images[i];
                    product[`url${i + 1}`] = image?.url
                    product[`altText${i + 1}`] = image?.altText || '';
                }

                const maxImages = 20; // Define the maximum number of images
                for (let i = 1; i <= maxImages; i++)
                {
                    group = new FieldGroupModel(`Image ${i}`);
                    var image = new FieldModel(`Image`, product[`url${i}`], "photo", `url${i}`, this.editActive, false);
                    image.allowDelete = true;
                    group.addField(image);
                    group.addField(new FieldModel(`Alt text (SEO)`, product[`altText${i}`], "text", `altText${i}`, this.editActive, false));
                    // group.singleColumn = true;
                    field_groups.push(group);
                }

                break;
        }
        return field_groups;
    }
    // #endregion

    // #region Vehicles

	public getExportSpecs()
	{
		this.exportService.getExportSpecs(false, false).subscribe(w =>
		{
			this.exportSpecs = w.map(v => new ListItemModel(v.id.toString(), v.caption.toString(), null, null, v));
			var a = w.filter(x => x.exportFlagsAllowAuctions == true);
			var b = w.filter(x => x.exportFlagsAllowAuctions == false);
			this.exportSpecsAuction = a.map(v => new ListItemModel(v.id.toString(), v.caption.toString(), null, null, v));
			this.exportSpecsNonAuction = b.map(v => new ListItemModel(v.id.toString(), v.caption.toString(), null, null, v));
		});
	}

    public countries: ListItemModel[];
    taxStatuses: ListItemModel[];//get full list of tax statuses
    exportSpecs: ListItemModel[];
    exportSpecsAuction: ListItemModel[];
    exportSpecsNonAuction: ListItemModel[];
    public fieldGroupTabs: TabModel[];
    field_groups: FieldGroupModel[];
    private vehicle: WebsiteVehicleModel;
    private editActive: boolean = false;
    public setupWebsiteVehicleFieldGroups(vehicle: WebsiteVehicleModel, editActive: boolean = false, makeDirty: boolean = false): TabModel[]
    {
        this.vehicle = vehicle;
        this.editActive = editActive;

        this.fieldGroupTabs = [];

        //Summary tab for people with no permissions
        // if (!editActive)
        // {
        //  this.fieldGroupTabs.push(new TabModel("summary", this.vehicle.vehicleSummary, this.getWebsiteVehicleFieldGroups("summary"), makeDirty, "summary", "", true, false));
        // }
        this.fieldGroupTabs.push(new TabModel("general", this.vehicle.vehicleGeneral, this.getWebsiteVehicleFieldGroups("general"), makeDirty, "general", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        this.fieldGroupTabs.push(new TabModel("purchase", this.vehicle.vehiclePurchase, this.getWebsiteVehicleFieldGroups("purchase"), makeDirty, "purchase", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        this.fieldGroupTabs.push(new TabModel("exterior", this.vehicle.vehicleExterior, this.getWebsiteVehicleFieldGroups("exterior"), makeDirty, "exterior", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        this.fieldGroupTabs.push(new TabModel("interior", this.vehicle.vehicleInterior, this.getWebsiteVehicleFieldGroups("interior"), makeDirty, "interior", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        this.fieldGroupTabs.push(new TabModel("mechanical", this.vehicle.vehicleMechanical, this.getWebsiteVehicleFieldGroups("mechanical"), makeDirty, "mechanical", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        this.fieldGroupTabs.push(new TabModel("export", this.vehicle.vehicleExport, this.getWebsiteVehicleFieldGroups("export"), makeDirty, "export", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        if (!this.editActive && this.userService.mcMinVersion("3.2.22")) this.fieldGroupTabs.push(new TabModel("Feature highlights", this.vehicle.vehicleExport, this.getWebsiteVehicleFieldGroups("highlights"), makeDirty, "highlights", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        this.fieldGroupTabs.push(new TabModel("admin", this.vehicle.vehicleAdmin, this.getWebsiteVehicleFieldGroups("admin"), makeDirty, "admin", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        this.fieldGroupTabs.push(new TabModel("media", this.vehicle.vehicleImages, this.getWebsiteVehicleFieldGroups("images"), makeDirty, "images", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));
        this.fieldGroupTabs.push(new TabModel("custom", this.vehicle.vehicleCustomFields, this.getWebsiteVehicleFieldGroups("custom"), makeDirty, "custom", "", this.userService.hasUserRight(SecurityRight.AdminWebsiteMan), this.userService.hasUserRight(SecurityRight.AdminWebsiteMan)));

        // console.log(this.fieldGroupTabs);
        return this.fieldGroupTabs;
    }

    public getWebsiteVehicleFieldGroups(groupId: string): FieldGroupModel[]
    {
        var field_groups: FieldGroupModel[] = [];
        var field_group: FieldGroupModel;
        switch (groupId)
        {
            case 'summary':

                field_group = new FieldGroupModel('Identification');

                field_group.addField(new FieldModel("VIN", this.vehicle.vehicleSummary.vin));
                field_group.addField(new FieldModel("Plate No.", this.vehicle.vehicleSummary.regNo));
                field_group.addField(new FieldModel("Ext. Colour", this.vehicle.vehicleSummary.extColorCaption));
                field_group.addField(new FieldModel("Int. Colour", this.vehicle.vehicleSummary.intColorCaption));
                field_groups.push(field_group);

                if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                {
                    field_group = new FieldGroupModel('Purchase');
                    field_group.addField(new FieldModel("Purchase Date", this.vehicle.vehiclePurchase.purchaseDate, "date"));
                    field_group.addField(new FieldModel("Purchase Type", this.vehicle.vehiclePurchase.purchaseType.caption));
                    field_group.addField(new FieldModel("Supplier", this.vehicle.vehiclePurchase.purchaseSupplierName));
                    field_groups.push(field_group);
                }


                field_group = new FieldGroupModel('Mechanical');
                field_group.addField(new FieldModel("Odometer", this.numberWithCommas(this.vehicle.vehicleSummary.odometerCaption), "text"));
                field_group.addField(new FieldModel("Engine Size", this.numberWithCommas(this.vehicle.vehicleSummary.engineSizeCaption), "text"));
                field_group.addField(new FieldModel("Transmission", this.numberWithCommas(this.vehicle.vehicleSummary.transmissionCaption), "text"));
                field_group.addField(new FieldModel("Fuel Type", this.numberWithCommas(this.vehicle.vehicleSummary.fuelTypeCaption), "text"));

                field_groups.push(field_group);

                //add permission-based summaru info

                if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                {

                }
                if (this.userService.hasUserRight(SecurityRight.VehiclesTabsExpensesView))
                {

                }


                field_group = new FieldGroupModel('Features');
                if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                {
                    field_group.addField(new FieldModel("Number of Doors", this.vehicle.vehicleExterior.doorNo));
                }
                if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                {
                    field_group.addField(new FieldModel('Number of Seats', this.vehicle.vehicleInterior.seatNo, 'number', 'seatNo', true, false, null));
                }
                if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                {
                    field_group.addField(new FieldModel('Exterior Features', this.vehicle.vehicleExterior.exteriorFeatureIds, 'lookups', 'exteriorFeatureIds', true, false, this.vehicle.vehicleExterior.exteriorFeatureLookups, '', this.vehicle.vehicleClass));
                }
                if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                {
                    if (this.vehicle.vehicleClass == VehicleClass.Caravan ||
                        this.vehicle.vehicleClass == VehicleClass.Machine ||
                        this.vehicle.vehicleClass == VehicleClass.Motorhome ||
                        this.vehicle.vehicleClass == VehicleClass.PassengerVehicle ||
                        this.vehicle.vehicleClass == VehicleClass.Boat)
                    {
                        field_group.addField(new FieldModel('Interior Features', this.vehicle.vehicleInterior.interiorFeatureIds, 'lookups', 'interiorFeatureIds', true, false, this.vehicle.vehicleInterior.interiorFeatureLookups, '', this.vehicle.vehicleClass));
                    }
                }
                if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                {
                    if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                    {
                        if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle || this.vehicle.vehicleClass == VehicleClass.Motorhome || this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                        {
                            if (!this.editActive && this.vehicle.vehicleGeneral.fuelSaverResult && this.vehicle.vehicleGeneral.fuelSaverResult.fuelSaverImage)
                            {
                                field_group.addField(new FieldModel('Fuel Economy Rating', Constants.ApiUrl + this.vehicle.vehicleGeneral.fuelSaverResult.fuelSaverImage, 'image', 'fuelSaverResult', false, false, null));
                            }
                        }
                    }

                    if (this.vehicle.vehicleClass == VehicleClass.Boat || this.vehicle.vehicleClass == VehicleClass.GoodsVehicle || this.vehicle.vehicleClass == VehicleClass.Machine || this.vehicle.vehicleClass == VehicleClass.Motorcycle || this.vehicle.vehicleClass == VehicleClass.Motorhome || this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                    {
                        var lookupModel = new LookupModel();
                        lookupModel.collection = "VehicleMechanicalFeatures";
                        lookupModel.lookupType = "external";
                        field_group.addField(new FieldModel('Mechanical Features', this.vehicle.vehicleMechanical.mechanicalFeatureIds, 'lookups', 'mechanicalFeatureIds', true, false, this.vehicle.vehicleMechanical.mechanicalFeatureLookups, '', this.vehicle.vehicleClass));
                    }
                }
                if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                {

                }

                field_groups.push(field_group);

                field_group = new FieldGroupModel('Admin');
                if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan) && this.vehicle.vehicleAdmin)
                {
                    field_group.addField(new FieldModel('Licensing Expiry Date', this.vehicle.vehicleAdmin.regExpiryDate, 'date', 'regExpiryDate', true, false, null));
                    field_group.addField(new FieldModel('WOF/COF Expiry Date', this.vehicle.vehicleAdmin.cofExpiryDate, 'date', 'cofExpiryDate', true, false, null));
                    field_group.addField(new FieldModel('Owner Dealership', this.vehicle.vehicleAdmin.companyId, 'contact', 'companyId', false, false, this.vehicle.vehicleAdmin.company));
                }
                // if (this.userService.hasUserRight(SecurityRight.AdminWebsiteMan))
                // {
                //     field_group.addField(new FieldModel('Appraised By', this.vehicle.vehiclePurchase.appraisedById, 'contact', 'appraisedById', true, false, this.vehicle.vehiclePurchase.appraisedBy));
                // }
                field_groups.push(field_group);
                break;
            case 'general':
                field_group = new FieldGroupModel('Make');

                var manufacturer = new FieldModel('Manufacturer', this.vehicle.vehicleGeneral.manufacturerId, 'lookup', 'manufacturerId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.Manufacturer), false, this.vehicle.vehicleGeneral.manufacturer, null, null, true);
                manufacturer.hasChildren = true;
                manufacturer.detailClass = this.vehicle.vehicleClass;
                field_group.addField(manufacturer);

                var model = new FieldModel('Model', this.vehicle.vehicleGeneral.modelId, 'lookup', 'modelId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.Model), false, this.vehicle.vehicleGeneral.model, null, null, true);
                model.parent = "manufacturerId";
                model.parentLookupId = this.vehicle.vehicleGeneral.manufacturerId;
                model.hasChildren = true;
                model.detailClass = this.vehicle.vehicleClass;
                field_group.addField(model);

                var variant = new FieldModel('Variant', this.vehicle.vehicleGeneral.variantId, 'lookup', 'variantId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.Variant), false, this.vehicle.vehicleGeneral.variant, null, null, true);
                variant.parent = "modelId";
                variant.parentLookupId = this.vehicle.vehicleGeneral.modelId;
                variant.detailClass = this.vehicle.vehicleClass;
                field_group.addField(variant);
                field_groups.push(field_group);

                field_group = new FieldGroupModel('Vehicle Year');
                field_group.addField(new FieldModel('Year', this.vehicle.vehicleGeneral.manuYear, 'text', 'manuYear', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.ManuYear), true, null, null, null, true));
                field_groups.push(field_group);

                field_group = new FieldGroupModel('Body');
                field_group.addField(new FieldModel('Body Style', this.vehicle.vehicleGeneral.bodyStyleId, 'lookup', 'bodyStyleId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.BodyStyle), false, this.vehicle.vehicleGeneral.bodyStyle, '', this.vehicle.vehicleClass, true));

                if (this.vehicle.vehicleClass == VehicleClass.Caravan ||
                    this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.Machine)
                {
                    field_group.addField(new FieldModel('Chassis Extras', this.vehicle.vehicleGeneral.chassisExtras, 'text', 'chassisExtras', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.ChassisExtras), false, null, null, null, true));
                    field_group.addField(new FieldModel('Chassis Type', this.vehicle.vehicleGeneral.chassisType, 'text', 'chassisType', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.ChassisType), false, null, null, null, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Boat)
                {
                    field_group.addField(new FieldModel('Hull Type', this.vehicle.vehicleGeneral.constructTypeId, 'lookup', 'constructTypeId', true, false, this.vehicle.vehicleGeneral.constructType));
                }
                field_groups.push(field_group);

                field_group = new FieldGroupModel('Registration');
                var registeredLocal = new FieldModel('Registered on the NZ Motor Vehicle Register', this.vehicle.vehicleGeneral.adminFlagsRegisteredLocal, 'toggle', 'adminFlagsRegisteredLocal', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.AdminFlagsRegisteredLocal), false, null, null, null, true);
                registeredLocal.hasChildren = true;
                field_group.addField(registeredLocal);

                var regNo = new FieldModel('Registration Plate No', this.vehicle.vehicleGeneral.regNo, 'text', 'regNo', (this.vehicle.vehicleGeneral.adminFlagsRegisteredLocal && !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.RegNo)), false, null, null, null, true);
                regNo.parent = 'adminFlagsRegisteredLocal';
                regNo.toggleEditableWhen = 'true';
                // regNo.hasButton = true;
                // regNo.buttonText = "Fetch Car Check details";
                // regNo.buttonIcon = "icon-CarCheck";
                // regNo.buttonName = "button-carcheck";
                field_group.addField(regNo);

                field_group.addField(new FieldModel('Re-Registered Vehicle', this.vehicle.vehicleGeneral.adminFlagsReRegisteredLocal, 'toggle', 'adminFlagsReRegisteredLocal', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.AdminFlagsReRegisteredLocal), false, null, null, null, true));
                field_group.addField(new FieldModel('Year First Registered NZ', this.vehicle.vehicleGeneral.regYearLocal, 'text', 'regYearLocal', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.RegYearLocal), false, null, null, null, true));
                field_groups.push(field_group);

                //field_group = new FieldGroupModel('Stock ID');
                //this.userService.hasUserRight(SecurityRight.VehiclesFunctionsEditStockNo) - for the time being, don't allow stockNo editing
                //field_group.addField(new FieldModel('Stock No', this.vehicle.vehicleGeneral.stockNo, 'text', 'stockNo', false, false, null));
                //field_groups.push(field_group);


                if (this.userService.mcMinVersion("3.2.13"))
                {
                    field_group = new FieldGroupModel('Hubometer');
                    field_group.addField(new FieldModel('Serial No', this.vehicle.vehicleGeneral.hubSerialNo, 'text', 'hubSerialNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.HubSerialNo), false, null, null, null, true));
                    field_group.addField(new FieldModel('Model No', this.vehicle.vehicleGeneral.hubModelNo, 'text', 'hubModelNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.HubModelNo), false, null, null, null, true));
                    field_groups.push(field_group);
                }


                field_group = new FieldGroupModel('Identification');

                var vin = new FieldModel('VIN', this.vehicle.vehicleGeneral.vin, 'text', 'vin', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.VIN), false, null, null, null, true);
                // vin.hasButton = true;
                // vin.buttonText = "Fetch Car Check details";
                // vin.buttonIcon = "icon-CarCheck";
                // vin.buttonName = "button-carcheck";
                field_group.addField(vin);

                field_group.addField(new FieldModel('Chassis No', this.vehicle.vehicleGeneral.chassisNo, 'text', 'chassisNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.ChassisNo), false, null, null, null, true));

                if (this.vehicle.vehicleClass != VehicleClass.Boat)
                {
                    field_group.addField(new FieldModel('Engine No', this.vehicle.vehicleGeneral.engineNo, 'text', 'engineNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineNo), false, null, null, null, true));
                }
                field_groups.push(field_group);

                field_group = new FieldGroupModel('Dimensions and Mass');
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.Motorcycle)
                {
                    field_group.addField(new FieldModel('Wheelbase (mm)', this.vehicle.vehicleGeneral.wheelbase, 'text', 'wheelbase', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.Wheelbase), false, null, null, null, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Boat)
                {
                    var unit = this.userService.mcMinVersion("3.2.0") ? '(m)' : '(cm)';
                    field_group.addField(new FieldModel('Length ' + unit, this.vehicle.vehicleGeneral.boatLength, 'decimal', 'boatLength', true, false, null));
                    field_group.addField(new FieldModel('Beam ' + unit, this.vehicle.vehicleGeneral.boatBeam, 'decimal', 'boatBeam', true, false, null));
                    field_group.addField(new FieldModel('Draft ' + unit, this.vehicle.vehicleGeneral.boatDraft, 'decimal', 'boatDraft', true, false, null));
                    field_group.addField(new FieldModel('Dry Weight (kg)', this.vehicle.vehicleGeneral.dryWeight, 'text', 'dryWeight', true, false, null));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Caravan)
                {
                    field_group.addField(new FieldModel('Caravan Length (ft)', this.vehicle.vehicleGeneral.caravanLength, 'decimal', 'caravanLength', true, false, null));
                    field_group.addField(new FieldModel('Caravan Width (ft)', this.vehicle.vehicleGeneral.caravanWidth, 'decimal', 'caravanWidth', true, false, null));
                }
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle)
                {
                    field_group.addField(new FieldModel('Deck Height (m)', this.vehicle.vehicleGeneral.deckHeight, 'decimal', 'deckHeight', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.DeckHeight), false, null, null, null, true));
                    field_group.addField(new FieldModel('Deck Width (m)', this.vehicle.vehicleGeneral.deckWidth, 'decimal', 'deckWidth', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.DeckWidth), false, null, null, null, true));
                    field_group.addField(new FieldModel('Deck Length (m)', this.vehicle.vehicleGeneral.deckLength, 'decimal', 'deckLength', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.DeckLength), false, null, null, null, true));
                    field_group.addField(new FieldModel('GVM (kg)', this.vehicle.vehicleGeneral.gvm, 'number', 'gvm', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.GVM), false, null, null, null, true));
                    field_group.addField(new FieldModel('TARE (kg)', this.vehicle.vehicleGeneral.tare, 'number', 'tare', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.Tare), false, null, null, null, true));
                    field_group.addField(new FieldModel('GLW (kg)', this.vehicle.vehicleGeneral.glw, 'number', 'glw', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.GLW), false, null, null, null, true));
                    field_group.addField(new FieldModel('GCW (kg)', this.vehicle.vehicleGeneral.gcw, 'number', 'gcw', true, false, null));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Motorcycle)
                {
                    field_group.addField(new FieldModel('Seat Height (mm)', this.vehicle.vehicleGeneral.seatHeight, 'text', 'seatHeight', true, false, null));
                    field_group.addField(new FieldModel('Ground Clearance (mm)', this.vehicle.vehicleGeneral.groundClearance, 'text', 'groundClearance', true, false, null));
                    field_group.addField(new FieldModel('Dry Weight (kg)', this.vehicle.vehicleGeneral.dryWeight, 'text', 'dryWeight', true, false, null));
                    field_group.addField(new FieldModel('Fuel Capacity (l)', this.vehicle.vehicleGeneral.fuelCapacity, 'text', 'fuelCapacity', true, false, null));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Motorhome)
                {
                    field_group.addField(new FieldModel('Motorhome Length (m)', this.vehicle.vehicleGeneral.motorhomeLength, 'decimal', 'motorhomeLength', true, false, null));
                    field_group.addField(new FieldModel('Motorhome Width (m)', this.vehicle.vehicleGeneral.motorhomeWidth, 'decimal', 'motorhomeWidth', true, false, null));
                }
                if (field_group.fields.length)
                {
                    field_groups.push(field_group);
                }

                if (!this.editActive &&
                    this.vehicle.vehicleGeneral.fuelSaverResult.reference)
                {
                    field_group = new FieldGroupModel('Ratings');

                    var fuelEconomy = new FieldModel('Fuel Economy', this.vehicle.vehicleGeneral.fuelSaverResult.fuelStars, 'stars', 'fuelRating', false);
                    fuelEconomy.detailObject = 6;
                    fuelEconomy.footernote = this.vehicle.vehicleGeneral.fuelSaverResult.fuelEconomyText + ((this.vehicle.vehicleGeneral.fuelSaverResult.fuelEconomyText) ? "; " : "") + this.vehicle.vehicleGeneral.fuelSaverResult.annFuelCostText;
                    field_group.addField(fuelEconomy);

                    var safetyStars = new FieldModel('Safety Rating', this.vehicle.vehicleGeneral.fuelSaverResult.driverSafetyStars, 'stars', 'fuelRating', false);
                    safetyStars.detailObject = 5;
                    safetyStars.footernote = this.vehicle.vehicleGeneral.fuelSaverResult.driverSafetyTest;
                    field_group.addField(safetyStars);

                    var cO2Stars = new FieldModel('Carbon Emissions', this.vehicle.vehicleGeneral.fuelSaverResult.cO2Stars, 'stars', 'fuelRating', false);
                    cO2Stars.detailObject = 6;
                    cO2Stars.footernote = this.vehicle.vehicleGeneral.fuelSaverResult.cO2Text + "; " + this.vehicle.vehicleGeneral.fuelSaverResult.cO2YearlyCaption;
                    field_group.addField(cO2Stars);

                    field_group.addField(new FieldModel('Last Query Date', this.vehicle.vehicleGeneral.fuelSaverResult.lastQueryDate, 'date', 'lastQueryDate', false));
                    field_group.addField(new FieldModel('Reference', this.vehicle.vehicleGeneral.fuelSaverResult.reference, 'text', 'reference', false));

                    field_groups.push(field_group);
                }
                break;
            case 'purchase':
                field_group = new FieldGroupModel('Purchase');
                field_group.addField(new FieldModel('Purchase Date', this.vehicle.vehiclePurchase.purchaseDate, 'date', 'purchaseDate', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.PurchaseDate), false, null, null, null, true));
                field_group.addField(new FieldModel('Purchase Type', this.vehicle.vehiclePurchase.purchaseTypeId, 'lookup', 'purchaseTypeId', ((this.vehicle.vehiclePurchase.purchaseType.caption == "Trade" ? false : true) && !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.PurchaseType)), false, this.vehicle.vehiclePurchase.purchaseType, null, null, true));

                // if (!this.vehicle.vehiclePurchase.vehicleLandedCost.includes("0.00") &&
                //     this.userService.hasUserRight(SecurityRight.VehiclesFunctionsAccessPurchaseCosts))
                // {
                //     field_group.addField(new FieldModel('Purchase Cost', this.vehicle.vehiclePurchase.vehicleLandedCost, 'text', 'landedCost', false, false, null));
                //     field_group.addField(new FieldModel('Purchase Cost Tax Amount', this.vehicle.vehiclePurchase.vehicleLandedCostGst, 'text', 'vehicleLandedCostGst', false, false, null));
                // }

                // if (this.vehicle.vehiclePurchase.vehicleWriteBack > 0)
                // {
                //     field_group.addField(new FieldModel('Vehicle Writeback', this.vehicle.vehiclePurchase.vehicleWriteBack, 'currency', 'vehicleWriteBack', false, false, null));
                // }

                // field_group.addField(new FieldModel('Supplier', this.vehicle.vehiclePurchase.purchaseSupplierName, 'text', 'purchaseSupplierName', true, false, null));
                // field_group.addField(new FieldModel('Trader Acquisition MR13C Applies', this.vehicle.vehiclePurchase.purchaseFlagsTraderAcquisitionApplies, 'toggle', 'purchaseFlagsTraderAcquisitionApplies', true, false, null));
                // field_group.addField(new FieldModel('Trader Acquisition Completed', this.vehicle.vehiclePurchase.purchaseFlagsTraderAcquisitionCompleted, 'toggle', 'purchaseFlagsTraderAcquisitionCompleted', true, false, null));
                // field_group.addField(new FieldModel('Purchase Notes', this.vehicle.vehiclePurchase.purchaseDesc, 'longtext', 'purchaseDesc', true, false, null));
                // field_group.addField(new FieldModel('Auction Grade', this.vehicle.vehiclePurchase.auctionGrade, 'text', 'auctionGrade', true, false, null));
                // field_group.addField(new FieldModel('Vehicle Appraisal Amount', this.vehicle.vehiclePurchase.appraisalAmount, 'currency', 'appraisalAmount', true, false, null));
                // field_group.addField(new FieldModel('Appraised By', this.vehicle.vehiclePurchase.appraisedById, 'contact', 'appraisedById', true, false, this.vehicle.vehiclePurchase.appraisedBy));
                // field_group.addField(new FieldModel('Appraisal Notes', this.vehicle.vehiclePurchase.appraisalNotes, 'longtext', 'appraisalNotes', true, false, null));
                // field_group.addField(new FieldModel('Vehicle Appraisal Tax Amount', this.vehicle.vehiclePurchase.appraisalTaxAmount, 'currency', 'appraisalTaxAmount', true, false, null));
                // field_group.addField(new FieldModel('Vehicle Appraisal Tax Rate', this.vehicle.vehiclePurchase.appraisalTaxRate, 'decimal', 'appraisalTaxRate', true, false, null, "percent"));
                // field_group.addField(new FieldModel('Vehicle Appraisal Tax Type', this.vehicle.vehiclePurchase.appraisalTaxStatusEnum, 'select', 'appraisalTaxStatusEnum', true, false, this.taxStatuses));
                // field_groups.push(field_group);

                field_group = new FieldGroupModel('Import');
                field_group.addField(new FieldModel('Delivery Port', this.vehicle.vehiclePurchase.deliveryPortId, 'lookup', 'deliveryPortId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.DeliveryPort), false, this.vehicle.vehiclePurchase.deliveryPort, null, null, true));
                field_group.addField(new FieldModel('Vessel Name', this.vehicle.vehiclePurchase.vesselName, 'text', 'vesselName', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.VesselName), false, null, null, null, true));
                field_group.addField(new FieldModel('Voyage', this.vehicle.vehiclePurchase.voyageName, 'text', 'voyageName', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.VoyageName), false, null, null, null, true));
                field_group.addField(new FieldModel('ETD', this.vehicle.vehiclePurchase.vesselDepartDate, 'date', 'vesselDepartDate', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.VesselDepartDate), false, null, null, null, true));
                field_group.addField(new FieldModel('ETA', this.vehicle.vehiclePurchase.portArrivalDate, 'date', 'portArrivalDate', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.PortArrivalDate), false, null, null, null, true));
                // field_group.addField(new FieldModel('Date on Yard', this.vehicle.vehiclePurchase.yardArrivalDate, 'date', 'yardArrivalDate', true, false, null));
                // field_group.addField(new FieldModel('Country Last Registered', this.vehicle.vehiclePurchase.prevCountryId, 'select', 'prevCountryId', true, false, this.countries));
                // field_group.addField(new FieldModel('Year First Registered', this.vehicle.vehiclePurchase.regYear, 'number', 'regYear', true, false, null));
                // field_group.addField(new FieldModel('Imported as Damaged Vehicle', this.vehicle.vehiclePurchase.purchaseFlagsImportedAsDamaged, 'toggle', 'purchaseFlagsImportedAsDamaged', true, false, null));
                // field_group.addField(new FieldModel('Days Since Purchase', this.vehicle.vehiclePurchase.daysSincePurchase, 'text', 'daysSincePurchase', true, false, null));
                field_groups.push(field_group);

                field_group = new FieldGroupModel('Owner History');
                field_group.addField(new FieldModel('Ownership Type', this.vehicle.vehiclePurchase.prevOwnerTypeId, 'lookup', 'prevOwnerTypeId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.PrevOwnerTypeId), false, this.vehicle.vehiclePurchase.prevOwnerType, '', this.vehicle.vehicleClass, true));
                var purchaseFlagsIsBrandNew = new FieldModel('Is Brand New', this.vehicle.vehiclePurchase.purchaseFlagsIsBrandNew, 'toggle', 'purchaseFlagsIsBrandNew', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.PurchaseFlagsIsBrandNew), false, null, null, null, true);
                purchaseFlagsIsBrandNew.hasChildren = true;
                field_group.addField(purchaseFlagsIsBrandNew);
                var prevOwnerNo = new FieldModel('No of Previous Owners', this.vehicle.vehiclePurchase.prevOwnerNo, 'number', 'prevOwnerNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.PrevOwnerNo), false, null, null, null, true);
                prevOwnerNo.parent = "purchaseFlagsIsBrandNew";
                prevOwnerNo.toggleEditableWhen = "false";
                field_group.addField(prevOwnerNo);
                field_group.addField(new FieldModel('Security Interest Currently Registered on PPSR', this.vehicle.vehiclePurchase.purchaseFlagsHasSecurityInterest, 'toggle', 'purchaseFlagsHasSecurityInterest', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.PurchaseFlagsHasSecurityInterest), false, null, null, null, true));
                field_groups.push(field_group);

                break;
            case 'expenses':
                field_group = new FieldGroupModel('Expenses');

                field_groups.push(field_group);
                break;
            case 'exterior':
                field_group = new FieldGroupModel('Exterior');

                this.vehicle.vehicleExterior.extColor1.allowAddCustomField1 = true;
                this.vehicle.vehicleExterior.extColor1.customField1Caption = 'HTML Colour Code';
                let primaryColour = new FieldModel('Primary Colour', this.vehicle.vehicleExterior.extColor1Id, 'lookup', 'extColor1Id', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.ExtColor1), false, this.vehicle.vehicleExterior.extColor1, '', this.vehicle.vehicleClass, true);
                field_group.addField(primaryColour);

                this.vehicle.vehicleExterior.extColor2.allowAddCustomField1 = true;
                this.vehicle.vehicleExterior.extColor2.customField1Caption = 'HTML Colour Code';
                let exteriorColour = new FieldModel('Secondary Colour', this.vehicle.vehicleExterior.extColor2Id, 'lookup', 'extColor2Id', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.ExtColor2), false, this.vehicle.vehicleExterior.extColor2, '', this.vehicle.vehicleClass, true);
                field_group.addField(exteriorColour);

                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Number of Doors', this.vehicle.vehicleExterior.doorNo, 'number', 'doorNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.DoorNo), false));
                    field_group.addField(new FieldModel('Sunroof', this.vehicle.vehicleExterior.sunroofId, 'lookup', 'sunroofId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.SunroofId), false, this.vehicle.vehicleExterior.sunroof, '', this.vehicle.vehicleClass, true));
                }
                field_groups.push(field_group);

                field_group = new FieldGroupModel('Wheels');
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.Motorcycle)
                {
                    field_group.addField(new FieldModel('Size', this.vehicle.vehicleExterior.wheelSize, 'decimal', 'wheelSize', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.WheelSize), false, null, null, null, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Motorcycle)
                {
                    field_group.addField(new FieldModel('Suspension', this.vehicle.vehicleExterior.suspension, 'text', 'suspension', true, false, null));
                    field_group.addField(new FieldModel('Brakes', this.vehicle.vehicleExterior.brakes, 'text', 'brakes', true, false, null));
                }
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Composition', this.vehicle.vehicleExterior.wheelCompId, 'lookup', 'wheelCompId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.WheelComp), false, this.vehicle.vehicleExterior.wheelComp, '', this.vehicle.vehicleClass, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle)
                {
                    field_group.addField(new FieldModel('Configuration', this.vehicle.vehicleExterior.wheelConfig, 'text', 'wheelConfig', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.WheelConfig), false, null, null, null, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle)
                {
                    field_group.addField(new FieldModel('Tyre Size', this.vehicle.vehicleExterior.tyreConfig, 'text', 'tyreConfig', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.TyreConfig), false, null, null, null, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Motorcycle)
                {
                    field_group.addField(new FieldModel('Tyres', this.vehicle.vehicleExterior.tyreConfig, 'text', 'tyreConfig', true, false, null));
                }
                if (this.vehicle.vehicleClass == VehicleClass.PassengerVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.Motorcycle)
                {
                    field_group.addField(new FieldModel('Tyre Condition', this.vehicle.vehicleExterior.tyreConditionId, 'lookup', 'tyreConditionId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.TyreCondition), false, this.vehicle.vehicleExterior.tyreCondition, '', this.vehicle.vehicleClass, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                field_group = new FieldGroupModel('Other Features');
                field_group.addField(new FieldModel('Other Features', this.vehicle.vehicleExterior.exteriorFeatureIds, 'lookups', 'exteriorFeatureIds', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.ExteriorFeatures), false, this.vehicle.vehicleExterior.exteriorFeatureLookups, '', this.vehicle.vehicleClass, true));
                field_groups.push(field_group);


                break;
            case 'interior':
                field_group = new FieldGroupModel('Interior');
                if (this.vehicle.vehicleClass == VehicleClass.Caravan ||
                    this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.Machine ||
                    this.vehicle.vehicleClass == VehicleClass.Motorhome ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.Boat)
                {
                    this.vehicle.vehicleInterior.intColor1.allowAddCustomField1 = true;
                    this.vehicle.vehicleInterior.intColor1.customField1Caption = 'Colour';
                    let interiorColor = new FieldModel('Interior Colour', this.vehicle.vehicleInterior.intColor1Id, 'lookup', 'intColor1Id', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.IntColor1), false, this.vehicle.vehicleInterior.intColor1, '', this.vehicle.vehicleClass, true);
                    field_group.addField(interiorColor);
                }
                if (this.vehicle.vehicleClass == VehicleClass.Boat)
                {
                    field_group.addField(new FieldModel('Number of Berths', this.vehicle.vehicleInterior.berthNo, 'number', 'berthNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.BerthNo), false, null, null, null, true));
                    field_group.addField(new FieldModel('Number of Cabins', this.vehicle.vehicleInterior.cabinNo, 'number', 'cabins', true, false, null));
                }
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Interior Trim', this.vehicle.vehicleInterior.intTrimId, 'lookup', 'intTrimId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.IntTrim), false, this.vehicle.vehicleInterior.intTrim, '', this.vehicle.vehicleClass, true));
                    field_group.addField(new FieldModel('Number of Seats', this.vehicle.vehicleInterior.seatNo, 'number', 'seatNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.SeatNo), false, null));
                    field_group.addField(new FieldModel('Seat Material', this.vehicle.vehicleInterior.seatMaterialId, 'lookup', 'seatMaterialId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.SeatMaterial), false, this.vehicle.vehicleInterior.seatMaterial, '', this.vehicle.vehicleClass, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                field_group = new FieldGroupModel('Audio');
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Cassette Player', this.vehicle.vehicleInterior.audioFlagsCassettePlayer, 'toggle', 'audioFlagsCassettePlayer', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.AudioFlagsCassettePlayer), false, null, null, null, true));
                    field_group.addField(new FieldModel('CD Player', this.vehicle.vehicleInterior.audioFlagsCDPlayer, 'toggle', 'audioFlagsCDPlayer', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.AudioFlagsCDPlayer), false, null, null, null, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.Motorcycle ||
                    this.vehicle.vehicleClass == VehicleClass.Motorhome ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Radio Receiver Capability', this.vehicle.vehicleInterior.audioFlagsBandExpander, 'toggle', 'audioFlagsBandExpander', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.AudioFlagsBandExpander), false, null, null, null, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                field_group = new FieldGroupModel('Safety');
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Number of Airbags', this.vehicle.vehicleInterior.airbagNo, 'number', 'airbagNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.AirbagNo), false, null, null, null, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                field_group = new FieldGroupModel('Berths');
                if (this.vehicle.vehicleClass == VehicleClass.Caravan)
                {
                    field_group.addField(new FieldModel('Number of Berths', this.vehicle.vehicleInterior.berthNo, 'number', 'berthNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.BerthNo), false, null, null, null, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                field_group = new FieldGroupModel('Other Features');
                if (this.vehicle.vehicleClass == VehicleClass.Caravan ||
                    this.vehicle.vehicleClass == VehicleClass.Machine ||
                    this.vehicle.vehicleClass == VehicleClass.Motorhome ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.Boat)
                {
                    field_group.addField(new FieldModel('Other Features', this.vehicle.vehicleInterior.interiorFeatureIds, 'lookups', 'interiorFeatureIds', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.InteriorFeatures), false, this.vehicle.vehicleInterior.interiorFeatureLookups, '', this.vehicle.vehicleClass, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                break;
            case 'mechanical':
                field_group = new FieldGroupModel('Engine');
                if (this.vehicle.vehicleClass == VehicleClass.Boat ||
                    this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.Machine ||
                    this.vehicle.vehicleClass == VehicleClass.Motorcycle ||
                    this.vehicle.vehicleClass == VehicleClass.Motorhome ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Engine Size', this.vehicle.vehicleMechanical.engineSize, 'decimal', 'engineSize', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineSize), false, null, null, null, true));
                    field_group.addField(new FieldModel('Engine Size Unit', this.vehicle.vehicleMechanical.engineSizeUnitId, 'lookup', 'engineSizeUnitId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineSizeUnit), false, this.vehicle.vehicleMechanical.engineSizeUnit, '', this.vehicle.vehicleClass, true));
                    var engineType = new FieldModel('Engine Type', this.vehicle.vehicleMechanical.engineType1Id, 'lookup', 'engineType1Id', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineType1), false, this.vehicle.vehicleMechanical.engineType1, '', this.vehicle.vehicleClass, true);
                    engineType.hasChildren = true;
                    field_group.addField(engineType);

                    this.vehicle.vehicleMechanical.transmission.allowAddCustomField1 = true;
                    this.vehicle.vehicleMechanical.transmission.customField1Caption = 'Display Short Code';
                    let transmission = new FieldModel('Transmission Type', this.vehicle.vehicleMechanical.transmissionId, 'lookup', 'transmissionId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.Transmission), false, this.vehicle.vehicleMechanical.transmission, '', this.vehicle.vehicleClass, true);
                    field_group.addField(transmission);

                    this.vehicle.vehicleMechanical.fuelType1.allowAddCustomField1 = true;
                    this.vehicle.vehicleMechanical.fuelType1.customField1Caption = 'Display Short Code';
                    let primaryFuelType = new FieldModel('Primary Fuel Type', this.vehicle.vehicleMechanical.fuelType1Id, 'lookup', 'fuelType1Id', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.FuelType1), false, this.vehicle.vehicleMechanical.fuelType1, '', this.vehicle.vehicleClass, true);
                    field_group.addField(primaryFuelType);

                    var secondaryFuelType = new FieldModel('Secondary Fuel Type', this.vehicle.vehicleMechanical.fuelType2Id, 'lookup', 'fuelType2Id', true, false, this.vehicle.vehicleMechanical.fuelType2, '', this.vehicle.vehicleClass, true);
                    secondaryFuelType.parent = 'engineType1Id';
                    secondaryFuelType.toggleEditableWhen = 'Hybrid|Plug-In Hybrid'; //Hybrid|Plug-in
                    secondaryFuelType.editable = (secondaryFuelType.toggleEditableWhen.indexOf(this.vehicle.vehicleMechanical.engineType1.caption) != -1) && !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.FuelType2);
                    secondaryFuelType.preventParentIdInLookup = true;


                    if (this.editActive || secondaryFuelType.editable)
                    {
                        field_group.addField(secondaryFuelType);
                    }
                }
                if (this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Number of Cylinders', this.vehicle.vehicleMechanical.cylinderNo, 'number', 'cylinderNo', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.CylinderNo), false, null, null, null, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Turbo', this.vehicle.vehicleMechanical.turboId, 'lookup', 'turboId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.Turbo), false, this.vehicle.vehicleMechanical.turbo, '', this.vehicle.vehicleClass, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Motorcycle ||
                    this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Drive System', this.vehicle.vehicleMechanical.driveSystemId, 'lookup', 'driveSystemId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.DriveSystem), false, this.vehicle.vehicleMechanical.driveSystem, '', this.vehicle.vehicleClass, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Motorcycle)
                {
                    field_group.addField(new FieldModel('Exhaust', this.vehicle.vehicleMechanical.exhaustType, 'text', 'exhaustType', true, false));
                    field_group.addField(new FieldModel('Engine Description', this.vehicle.vehicleMechanical.engineDescription, 'text', 'engineDescription', true, false));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Boat)
                {
                    field_group.addField(new FieldModel('Manufacturer', this.vehicle.vehicleMechanical.engineManuId, 'lookup', 'engineManuId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineManufacturer), false, this.vehicle.vehicleMechanical.engineManu, '', this.vehicle.vehicleClass, true));
                    field_group.addField(new FieldModel('Model', this.vehicle.vehicleMechanical.engineModel, 'text', 'engineModel', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineModel), false, null, null, null, true));
                    field_group.addField(new FieldModel('Engine Year', this.vehicle.vehicleMechanical.engineYear, 'text', 'engineYear', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineYear), false, null, null, null, true));
                    field_group.addField(new FieldModel('Engine Hours', this.vehicle.vehicleMechanical.engineHours, 'decimal', 'engineHours', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineHours), false, null, null, null, true));
                    field_group.addField(new FieldModel('Drive System', this.vehicle.vehicleMechanical.driveSystemId, 'lookup', 'driveSystemId', true, false, this.vehicle.vehicleMechanical.driveSystem, '', this.vehicle.vehicleClass));
                    field_group.addField(new FieldModel('Number of Engines', this.vehicle.vehicleMechanical.engineNo, 'number', 'engineNo', true, false, null));
                }
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle)
                {
                    field_group.addField(new FieldModel('Manufacturer', this.vehicle.vehicleMechanical.engineManuId, 'lookup', 'engineManuId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineManufacturer), false, this.vehicle.vehicleMechanical.engineManu, '', this.vehicle.vehicleClass, true));
                    field_group.addField(new FieldModel('Model', this.vehicle.vehicleMechanical.engineModel, 'text', 'engineModel', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineModel), false, null, null, null, true));
                    field_group.addField(new FieldModel('BHP Rating', this.vehicle.vehicleMechanical.engineHp, 'decimal', 'engineHp', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineHP), false, null, null, null, true));
                    field_group.addField(new FieldModel('Torque', this.vehicle.vehicleMechanical.engineTorqueId, 'lookup', 'engineTorqueId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.EngineTorque), false, this.vehicle.vehicleMechanical.engineTorque, '', this.vehicle.vehicleClass, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                if (this.vehicle.vehicleClass == VehicleClass.Boat)
                {
                    field_group = new FieldGroupModel('Ratings');
                    field_group.addField(new FieldModel('Max Speed (knots)', this.vehicle.vehicleMechanical.maxSpeed, 'decimal', 'maxSpeed', true, false, null));
                    field_group.addField(new FieldModel('Cruise Speed (knots)', this.vehicle.vehicleMechanical.cruiseSpeed, 'decimal', 'cruiseSpeed', true, false, null));
                    field_group.addField(new FieldModel('Fuel Capacity (l)', this.vehicle.vehicleMechanical.fuelCapacity, 'decimal', 'fuelCapacity', true, false, null));
                    field_group.addField(new FieldModel('Water Capacity (l)', this.vehicle.vehicleMechanical.waterCapacity, 'decimal', 'waterCapacity', true, false, null));
                    if (field_group.fields.length) field_groups.push(field_group);
                }

                field_group = new FieldGroupModel('Transmission');
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle || this.vehicle.vehicleClass == VehicleClass.Machine || this.vehicle.vehicleClass == VehicleClass.Motorcycle || this.vehicle.vehicleClass == VehicleClass.Motorhome || this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    this.vehicle.vehicleMechanical.transmission.allowAddReference = true;
                    this.vehicle.vehicleMechanical.transmission.referenceCaption = 'Display Short Code';

                    this.vehicle.vehicleMechanical.transmission.allowAddCustomField1 = true;
                    this.vehicle.vehicleMechanical.transmission.customField1Caption = 'Gears';

                    let transmission = new FieldModel('Transmission Type', this.vehicle.vehicleMechanical.transmissionId, 'lookup', 'transmissionId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.Transmission), false, this.vehicle.vehicleMechanical.transmission, '', this.vehicle.vehicleClass, true);
                    field_group.addField(transmission);
                }
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle)
                {
                    field_group.addField(new FieldModel('Model', this.vehicle.vehicleMechanical.transModel, 'text', 'transModel', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.TransmissionModel), false, null));
                    field_group.addField(new FieldModel('Manufacturer', this.vehicle.vehicleMechanical.transManuId, 'lookup', 'transManuId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.TransmissionManufacturer), false, this.vehicle.vehicleMechanical.transManu, '', this.vehicle.vehicleClass, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                if (this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group = new FieldGroupModel('Electric Battery');
                    field_group.addField(new FieldModel('Battery Power (kWh)', this.vehicle.vehicleMechanical.batPower, 'decimal', 'batPower', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.BatPower), false, null, null, null, true));
                    field_group.addField(new FieldModel('Battery Range (km)', this.vehicle.vehicleMechanical.batRange, 'decimal', 'batRange', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.BatRange), false, null, null, null, true));
                    field_group.addField(new FieldModel('SOC/SOH (%)', this.vehicle.vehicleMechanical.batHealth, 'decimal', 'batHealth', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.BatHealth), false, null, null, null, true));
                    field_groups.push(field_group);
                }

                field_group = new FieldGroupModel('Odometer');
                if (this.vehicle.vehicleClass == VehicleClass.GoodsVehicle || this.vehicle.vehicleClass == VehicleClass.Machine || this.vehicle.vehicleClass == VehicleClass.Motorcycle || this.vehicle.vehicleClass == VehicleClass.Motorhome || this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Odometer Status', this.vehicle.vehicleMechanical.odoStatusId, 'lookup', 'odoStatusId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.OdoStatus), false, this.vehicle.vehicleMechanical.odoStatus, '', this.vehicle.vehicleClass, true));
                    field_group.addField(new FieldModel('Odometer Reading', this.vehicle.vehicleMechanical.odoReading, 'decimal', 'odoReading', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.OdoReading), false, null, null, null, true));
                    field_group.addField(new FieldModel('Odo Unit', this.vehicle.vehicleMechanical.odoUnitId, 'lookup', 'odoUnitId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.OdoUnit), false, this.vehicle.vehicleMechanical.odoUnit, '', this.vehicle.vehicleClass, true));
                    field_group.addField(new FieldModel('Odometer Reading Date', this.vehicle.vehicleMechanical.odoDate, 'date', 'odoDate', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.OdoDate), false, null, null, null, true));
                }
                if (this.vehicle.vehicleClass == VehicleClass.Machine || this.vehicle.vehicleClass == VehicleClass.Motorcycle || this.vehicle.vehicleClass == VehicleClass.Motorhome || this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('Odometer Certified', this.vehicle.vehicleMechanical.mechanicalFlagsOdometerCertified, 'toggle', 'mechanicalFlagsOdometerCertified', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.MechanicalFlagsOdometerCertified), false, null, null, null, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                field_group = new FieldGroupModel('Safety');
                if (this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    field_group.addField(new FieldModel('ABS Braking', this.vehicle.vehicleMechanical.mechanicalFlagsABSBraking, 'toggle', 'mechanicalFlagsABSBraking', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.MechanicalFlagsABSBraking), false, null, null, null, true));
                    field_group.addField(new FieldModel('Traction Control', this.vehicle.vehicleMechanical.mechanicalFlagsTractionControl, 'toggle', 'mechanicalFlagsTractionControl', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.MechanicalFlagsTractionControl), false, null, '', this.vehicle.vehicleClass, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                field_group = new FieldGroupModel('Other Features');
                if (this.vehicle.vehicleClass == VehicleClass.Boat || this.vehicle.vehicleClass == VehicleClass.GoodsVehicle || this.vehicle.vehicleClass == VehicleClass.Machine || this.vehicle.vehicleClass == VehicleClass.Motorcycle || this.vehicle.vehicleClass == VehicleClass.Motorhome || this.vehicle.vehicleClass == VehicleClass.PassengerVehicle)
                {
                    var lookupModel = new LookupModel();
                    lookupModel.collection = "VehicleMechanicalFeatures";
                    lookupModel.lookupType = "external";
                    field_group.addField(new FieldModel('Other Features', this.vehicle.vehicleMechanical.mechanicalFeatureIds, 'lookups', 'mechanicalFeatureIds', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.MechanicalFeatures), false, this.vehicle.vehicleMechanical.mechanicalFeatureLookups, '', this.vehicle.vehicleClass, true));
                }
                if (field_group.fields.length) field_groups.push(field_group);

                break;
            case 'export':
                field_group = new FieldGroupModel('Export Settings');
                var adminFlagsIncludeExport = new FieldModel('Include in All Exports', this.vehicle.vehicleExport.adminFlagsIncludeExport, 'toggle', 'adminFlagsIncludeExport', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.AdminFlagsIncludeExport), false, null, null, null, true);
                adminFlagsIncludeExport.hasChildren = true;
                field_group.addField(adminFlagsIncludeExport);

                if (this.editActive || this.vehicle.vehicleExport.adminFlagsIncludeExport == false)
                {
                    var exportSpecs = new FieldModel('Specify Exports where this Vehicle is included', this.vehicle.vehicleExport.exportSpecIds, 'selects', 'exportSpecIds', this.vehicle.vehicleExport.adminFlagsIncludeExport == false, false, this.exportSpecsNonAuction, '', this.vehicle.vehicleClass)
                    exportSpecs.parent = 'adminFlagsIncludeExport';
                    exportSpecs.toggleEditableWhen = 'false';
                    field_group.addField(exportSpecs);
                }

                field_groups.push(field_group);

                // field_group = new FieldGroupModel('Need a Car');
                // field_group.addField(new FieldModel('Highlight this Vehicle', this.vehicle.vehicleExport.adminFlagsIsFeaturedNeedacar, 'toggle', 'adminFlagsIsFeaturedNeedacar', true, false, null));
                // field_groups.push(field_group);

                // field_group = new FieldGroupModel('Trade Me');
                // field_group.addField(new FieldModel('Feature this Vehicle', this.vehicle.vehicleExport.adminFlagsIsFeatured, 'toggle', 'adminFlagsIsFeatured', true, false, null));
                // field_group.addField(new FieldModel('Top Pick this Vehicle', this.vehicle.vehicleExport.adminFlagsIsShowroomTradeMe, 'toggle', 'adminFlagsIsShowroomTradeMe', true, false, null));
                // field_groups.push(field_group);

                // field_group = new FieldGroupModel('Export Vehicle as an Auction');
                // var chkAuctionExport = new FieldModel('Export this vehicle as an Auction', this.vehicle.vehicleExport.chkAuctionExport, 'toggle', 'chkAuctionExport', true, false, null);
                // chkAuctionExport.hasChildren = true;
                // field_group.addField(chkAuctionExport);

                // if (this.editActive || this.vehicle.vehicleExport.chkAuctionExport)
                // {
                //     var auctionExportId = new FieldModel('Export as an Auction to', this.vehicle.vehicleExport.auctionExportId, 'select', 'auctionExportId', this.vehicle.vehicleExport.chkAuctionExport, false, this.exportSpecsAuction);
                //     auctionExportId.parent = 'chkAuctionExport';
                //     auctionExportId.toggleEditableWhen = 'true';
                //     field_group.addField(auctionExportId);
                // }

                // field_group.addField(new FieldModel('Start Price', this.vehicle.vehicleExport.startPriceAmount, 'currency', 'startPriceAmount', true, false, null));
                // field_group.addField(new FieldModel('Reserve', this.vehicle.vehicleExport.reservePriceAmount, 'currency', 'reservePriceAmount', true, false, null));
                // field_group.addField(new FieldModel('Now', this.vehicle.vehicleExport.buyNowPriceAmount, 'currency', 'buyNowPriceAmount', true, false, null));
                // field_groups.push(field_group);

                field_group = new FieldGroupModel('Additional Export Information');
                field_group.addField(new FieldModel('Title', this.vehicle.vehicleExport.exportTitle, 'text', 'exportTitle', true, false, null));
                field_group.addField(new FieldModel('Tagline / Subtitle', this.vehicle.vehicleExport.exportTagline, 'text', 'exportTagline', true, false, null));
                field_group.addField(new FieldModel('Alternate Variant', this.vehicle.vehicleExport.variantAlt, 'text', 'variantAlt', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.VariantAlt), false, null, null, null, true));
                // field_group.addField(new FieldModel('Alternate Variant', this.vehicle.vehicleExport.variantAlt, 'text', 'variantAlt', true, false, null));
                field_group.addField(new FieldModel('Alternate Stock No', this.vehicle.vehicleExport.stockNoAlt, 'text', 'stockNoAlt', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.StockNoAlt), false, null, null, null, true));
                // field_group.addField(new FieldModel('Alternative Stock No', this.vehicle.vehicleExport.stockNoAlt, 'text', 'stockNoAlt', true, false, null));
                field_group.addField(new FieldModel('Details / Comments', this.vehicle.vehicleExport.exportDescription, 'longtext', 'exportDescription', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.Description), false, null, null, null, true));
                field_groups.push(field_group);

                field_group = new FieldGroupModel('Vehicle Offer');
				var displayOffer = new FieldModel('Display an offer on this vehicle', this.vehicle.vehicleExport.displayOffer, 'toggle', 'displayOffer', true, false, null);
                displayOffer.hasChildren = true;
                field_group.addField(displayOffer);

				var expiryDate = new FieldModel('Expiry Date', this.vehicle.vehicleExport.offerEndDate, 'date', 'offerEndDate', true, true, null, null, null, true);
                expiryDate.parent = 'displayOffer';
                expiryDate.toggleEditableWhen = 'true';
                expiryDate.editable = (this.vehicle.vehicleExport.displayOffer == true) && !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.OfferEndDate);
                field_group.addField(expiryDate);

				var offerText = new FieldModel('Offer Text', this.vehicle.vehicleExport.offerText, 'text', 'offerText', true, true, null, null, null, true);
                offerText.parent = 'displayOffer';
                offerText.toggleEditableWhen = 'true';
                offerText.editable = (this.vehicle.vehicleExport.displayOffer == true) && !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.OfferText);
                field_group.addField(offerText);
                field_groups.push(field_group);

                break;
            case 'admin':
                field_group = new FieldGroupModel('Current Status');
                var statusSelector = new FieldModel('Status', this.vehicle.vehicleAdmin.vehicleStatusId, 'lookup', 'vehicleStatusId', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.VehicleStatus), false, this.vehicle.vehicleAdmin.vehicleStatus, '', this.vehicle.vehicleClass, true);
                statusSelector.showBoxes = true;
                statusSelector.hasChildren = true;
                field_group.addField(statusSelector);

                var vehicleStatusSupId = new FieldModel('Supplier', this.vehicle.vehicleAdmin.vehicleStatusSupId, 'contact', 'vehicleStatusSupId', true, false, this.vehicle.vehicleAdmin.vehicleStatusSup);
                vehicleStatusSupId.parent = 'vehicleStatusId';
                vehicleStatusSupId.toggleEditableWhen = "0cc33a04-ed83-4363-ad2b-1f1f988d4ef9";
                vehicleStatusSupId.editable = (this.vehicle.vehicleAdmin.vehicleStatusId == "0cc33a04-ed83-4363-ad2b-1f1f988d4ef9");


                var vehicleStatusDesc = new FieldModel('Status Comment', this.vehicle.vehicleAdmin.vehicleStatusDesc, 'longtext', 'vehicleStatusDesc', true, false, null);
                vehicleStatusDesc.parent = 'vehicleStatusId';
                vehicleStatusDesc.toggleEditableWhen = "0cc33a04-ed83-4363-ad2b-1f1f988d4ef9";
                vehicleStatusDesc.editable = (this.vehicle.vehicleAdmin.vehicleStatusId != "0cc33a04-ed83-4363-ad2b-1f1f988d4ef9");
                vehicleStatusDesc.inverseCheck = true;

                if (this.editActive || vehicleStatusSupId.editable)
                {
                    field_group.addField(vehicleStatusSupId);
                }
                if (this.editActive || vehicleStatusDesc.editable)
                {
                    field_group.addField(vehicleStatusDesc);
                }

                if (!this.editActive)
                {
                    var statusHistory = new FieldModel('View status history', '', 'button', 'viewStatusHistory', false, false, null, "button-status-history");
                    statusHistory.buttonExpand = 'block';
                    statusHistory.buttonSize = 'small';
                    field_group.addField(statusHistory);
                }


                field_groups.push(field_group);

                if (!this.editActive)
                {
                    field_group = new FieldGroupModel('Dealership');
                    field_group.addField(new FieldModel('Owner Dealership', this.vehicle.vehicleAdmin.companyId, 'contact', 'companyId', true, false, this.vehicle.vehicleAdmin.company));

                    if (this.userService.mcMinVersion("3.2.21"))
                    {
                        var transferVehicle = new FieldModel('Transfer vehicle', '', 'button', 'transferVehicle', false, false, null, "button-transfer-vehicle");
                        transferVehicle.buttonExpand = 'block';
                        transferVehicle.buttonSize = 'small';
                        field_group.addField(transferVehicle);
                    }

                    var transferHistory = new FieldModel('View transfer history', '', 'button', 'viewTransferHistory', false, false, null, "button-transfer-history");
                    transferHistory.buttonExpand = 'block';
                    transferHistory.buttonSize = 'small';
                    field_group.addField(transferHistory);
                    
                    field_groups.push(field_group);
                }
                    

                field_group = new FieldGroupModel('Licensing');
                field_group.addField(new FieldModel('Licensing Expiry Date', this.vehicle.vehicleAdmin.regExpiryDate, 'date', 'regExpiryDate', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.RegExpiryDate), false, null, null, null, true));
                // var adminFlagsRoadUserChargesApply = new FieldModel('Road User Charges Apply', this.vehicle.vehicleAdmin.adminFlagsRoadUserChargesApply, 'toggle', 'adminFlagsRoadUserChargesApply', true, false, null, null)
                // adminFlagsRoadUserChargesApply.hasChildren = true;
                // field_group.addField(adminFlagsRoadUserChargesApply);

                // var adminFlagsRoadUserChargesOutstanding = new FieldModel('Outstanding Road User charges', this.vehicle.vehicleAdmin.adminFlagsRoadUserChargesOutstanding, 'toggle', 'adminFlagsRoadUserChargesOutstanding', true, false, null)
                // adminFlagsRoadUserChargesOutstanding.parent = "adminFlagsRoadUserChargesApply";
                // adminFlagsRoadUserChargesOutstanding.toggleEditableWhen = "true;"
                // field_group.addField(adminFlagsRoadUserChargesOutstanding);

                // var roadUserEnd = new FieldModel('RUC End Distance', this.vehicle.vehicleAdmin.roadUserEnd, 'text', 'roadUserEnd', true, false, null);
                // roadUserEnd.parent = 'adminFlagsRoadUserChargesApply';
                // roadUserEnd.toggleEditableWhen = "true";
                // field_group.addField(roadUserEnd);

                field_group.addField(new FieldModel('WOF/COF Expiry Date', this.vehicle.vehicleAdmin.cofExpiryDate, 'date', 'cofExpiryDate', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.COFExpiryDate), false, null, null, null, true));
                field_groups.push(field_group);

                // field_group = new FieldGroupModel('Notes');
                // field_group.addField(new FieldModel('Notes', this.vehicle.vehicleAdmin.comment, 'longtext', 'comment', true, false, null));
                // field_groups.push(field_group);

                field_group = new FieldGroupModel('Pricing');

                var retailPriceObject = new PriceEditorModel();
                retailPriceObject.price = { id: "retailPriceAmount", value: this.vehicle.vehicleAdmin.retailPriceAmount };
                retailPriceObject.taxType = { id: "retailPriceTaxStatusEnum", value: this.vehicle.vehicleAdmin.retailPriceTaxStatusEnum };
                if (this.userService.mcMinVersion("3.2.22"))
                {
                    retailPriceObject.mode = "extended";
                    retailPriceObject.editableTaxRate = false;
                    retailPriceObject.currency = { id: "retailPriceCurrencyId", value: "" };
                    retailPriceObject.taxRate = { id: "priceTaxRate", value: this.vehicle.vehicleAdmin.priceTaxRate };
                    retailPriceObject.taxAmount = { id: "retailPriceTaxAmount", value: this.vehicle.vehicleAdmin.retailPriceTaxAmount };
                }

                var retailIncludesAnyReadOnly = this.vehicle.readOnlyProperties?.some(field =>
                    field === ExternalProcessorControlledField.RetailPriceAmount ||
                    field === ExternalProcessorControlledField.RetailPriceTaxStatusEnum ||
                    field === ExternalProcessorControlledField.PriceTaxRate ||
                    field === ExternalProcessorControlledField.RetailPriceTaxAmount
                );
                field_group.addField(new FieldModel("Retail price", this.vehicle.vehicleAdmin.retailPriceAmount, "price", "retailPriceAmount", !retailIncludesAnyReadOnly, false, retailPriceObject, null, null, true));

                var wholesalePriceObject = new PriceEditorModel();
                wholesalePriceObject.price = { id: "wholesalePriceAmount", value: this.vehicle.vehicleAdmin.wholesalePriceAmount };
                wholesalePriceObject.taxType = { id: "wholesalePriceTaxStatusEnum", value: this.vehicle.vehicleAdmin.wholesalePriceTaxStatusEnum };
                if (this.userService.mcMinVersion("3.2.22"))
                {
                    wholesalePriceObject.mode = "extended";
                    wholesalePriceObject.editableTaxRate = false;
                    wholesalePriceObject.currency = { id: "wholesalePriceCurrencyId", value: "" };
                    wholesalePriceObject.taxRate = { id: "priceTaxRate", value: this.vehicle.vehicleAdmin.priceTaxRate };
                    wholesalePriceObject.taxAmount = { id: "wholesalePriceTaxAmount", value: this.vehicle.vehicleAdmin.wholesalePriceTaxAmount };
                }
                var wholesaleIncludesAnyReadOnly = this.vehicle.readOnlyProperties?.some(field =>
                    field === ExternalProcessorControlledField.WholesalePrice ||
                    field === ExternalProcessorControlledField.WholesalePriceTaxStatusEnum ||
                    field === ExternalProcessorControlledField.PriceTaxRate ||
                    field === ExternalProcessorControlledField.WholesalePriceTaxAmount
                );
                field_group.addField(new FieldModel("Wholesale price", this.vehicle.vehicleAdmin.wholesalePriceAmount, "price", "wholesalePriceAmount", !wholesaleIncludesAnyReadOnly, false, wholesalePriceObject, null, null, true));

                var specialPriceObject = new PriceEditorModel();
                specialPriceObject.price = { id: "specialPriceAmount", value: this.vehicle.vehicleAdmin.specialPriceAmount };
                specialPriceObject.taxType = { id: "specialPriceTaxStatusEnum", value: this.vehicle.vehicleAdmin.specialPriceTaxStatusEnum };
                if (this.userService.mcMinVersion("3.2.22"))
                {
                    specialPriceObject.mode = "extended";
                    specialPriceObject.editableTaxRate = false;
                    specialPriceObject.currency = { id: "specialPriceCurrencyId", value: "" };
                    specialPriceObject.taxRate = { id: "priceTaxRate", value: this.vehicle.vehicleAdmin.priceTaxRate };
                    specialPriceObject.taxAmount = { id: "specialPriceTaxAmount", value: this.vehicle.vehicleAdmin.specialPriceTaxAmount };
                }
                var specialPriceIncludesAnyReadOnly = this.vehicle.readOnlyProperties?.some(field =>
                    field === ExternalProcessorControlledField.OtherPrice1Amount ||
                    field === ExternalProcessorControlledField.OtherPrice1TaxStatusEnum ||
                    field === ExternalProcessorControlledField.PriceTaxRate ||
                    field === ExternalProcessorControlledField.OtherPrice1TaxAmount
                );
                field_group.addField(new FieldModel("Special price", this.vehicle.vehicleAdmin.specialPriceAmount, "price", "specialPriceAmount", !specialPriceIncludesAnyReadOnly, false, specialPriceObject, null, null, true));

                var orcPriceObject = new PriceEditorModel();
                orcPriceObject.price = { id: "orcPriceAmount", value: this.vehicle.vehicleAdmin.orcPriceAmount };
                orcPriceObject.taxType = { id: "orcPriceTaxStatusEnum", value: this.vehicle.vehicleAdmin.orcPriceTaxStatusEnum };
                if (this.userService.mcMinVersion("3.2.22"))
                {
                    orcPriceObject.mode = "extended";
                    orcPriceObject.editableTaxRate = false;
                    orcPriceObject.currency = { id: "orcPriceCurrencyId", value: "" };
                    orcPriceObject.taxRate = { id: "priceTaxRate", value: this.vehicle.vehicleAdmin.priceTaxRate };
                    orcPriceObject.taxAmount = { id: "orcPriceTaxAmount", value: this.vehicle.vehicleAdmin.orcPriceTaxAmount };
                }
                var orcPriceIncludesAnyReadOnly = this.vehicle.readOnlyProperties?.some(field =>
                    field === ExternalProcessorControlledField.OtherPrice2Amount ||
                    field === ExternalProcessorControlledField.OtherPrice2TaxStatusEnum ||
                    field === ExternalProcessorControlledField.PriceTaxRate ||
                    field === ExternalProcessorControlledField.OtherPrice2TaxAmount
                );
                field_group.addField(new FieldModel("On road costs", this.vehicle.vehicleAdmin.orcPriceAmount, "price", "orcPriceAmount", !orcPriceIncludesAnyReadOnly, false, orcPriceObject, null, null, true));

                field_group.addField(new FieldModel('Don\'t Promote Finance on this Vehicle', this.vehicle.vehicleAdmin.adminFlagsDisableFinance, 'toggle', 'adminFlagsDisableFinance', !this.vehicle.readOnlyProperties?.includes(ExternalProcessorControlledField.AdminFlagsDisableFinance), false, null, null, null, true));

                // if (this.userService.mcMinVersion("3.2.19"))
                // {
                //     var priceHistory = new FieldModel('View pricing history', '', 'button', 'viewPriceHistory', false, false, null, "button-price-history");
                //     priceHistory.buttonExpand = 'block';
                //     priceHistory.buttonSize = 'small';
                //     field_group.addField(priceHistory);
                // }

                field_groups.push(field_group);

                // console.log(field_groups)


                // if (!this.editActive &&
                //     this.vehicle.vehicleGeneral.fuelSaverResult.reference &&
                //     this.userService.mcMinVersion("3.2.11"))
                // {
                //     field_group = new FieldGroupModel('Clean Car Details');


                //     var rebateText = "";
                //     switch (this.vehicle.vehicleGeneral.fuelSaverResult.cleanVehicleInd)
                //     {

                //         case "Not eligible":
                //         case "No rebate or Fee":
                //             rebateText = this.vehicle.vehicleGeneral.fuelSaverResult.cO2Disclaimer;
                //             break;
                //         case "Possible Fee":
                //         case "Fee":
                //         case "Possible Rebate":
                //         case "Rebate":
                //             rebateText = this.vehicle.vehicleGeneral.fuelSaverResult.cleanVehicleText;
                //             break;
                //     }

                //     field_group.addField(new FieldModel('Clean Car Fee/Rebate', rebateText, 'text', 'rebateText', true, false, null));
                //     // field_group.addField(new FieldModel('Amount', this.numberWithCommas(this.vehicle.vehicleGeneral.fuelSaverResult.cleanVehicleAmount), 'currency',  null, true, false, null));
                //     // field_group.addField(new FieldModel('Amount Max', this.numberWithCommas(this.vehicle.vehicleGeneral.fuelSaverResult.cleanVehicleAmountMax), 'currency',  null, true, false, null));
                //     // field_group.addField(new FieldModel('Amount Max', this.numberWithCommas(this.vehicle.vehicleAdmin), 'currency',  null, true, false, null));
                //     field_groups.push(field_group);
                // }

                // field_group = new FieldGroupModel('Vehicle Groups');
                // var adminFlagsIncludeGroups = new FieldModel('Include in All Groups', this.vehicle.vehicleAdmin.adminFlagsIncludeGroups, 'toggle', 'adminFlagsIncludeGroups', true, false, null)
                // adminFlagsIncludeGroups.hasChildren = true;
                // field_group.addField(adminFlagsIncludeGroups);
                // var vehicleGroupIds = new FieldModel('Specify Groups where this Vehicle is included', this.vehicle.vehicleAdmin.vehicleGroupIds, 'lookups', 'vehicleGroupIds', true, false, this.vehicle.vehicleAdmin.vehicleGroups);
                // vehicleGroupIds.parent = 'adminFlagsIncludeGroups';
                // vehicleGroupIds.toggleEditableWhen = 'false';
                // field_group.addField(vehicleGroupIds);
                // field_groups.push(field_group);

                break;

            case 'images':
                field_group = new FieldGroupModel('Image Overlays');

                var firstImage = new FieldModel('First Image', this.vehicle.vehicleImages.overlay1Id, 'lookup', 'overlay1Id', true, false, this.vehicle.vehicleImages.overlay1, '', this.vehicle.vehicleClass);
                firstImage.showBoxes = true;
                firstImage.hasChildren = true;
                field_group.addField(firstImage);

                var otherImages = new FieldModel('Other Images', this.vehicle.vehicleImages.overlay2Id, 'lookup', 'overlay2Id', true, false, this.vehicle.vehicleImages.overlay2, '', this.vehicle.vehicleClass);
                otherImages.showBoxes = true;
                otherImages.hasChildren = true;
                field_group.addField(otherImages);

                //add a link
                var imageOverlayLink = new FieldModel('', 'About image overlays', 'url', 'overlayLink', true, false, 'https://support.motorcentral.co.nz/hc/en-us/articles/213883163-Using-Vehicle-Image-Overlays', '', '', true);
                imageOverlayLink.hideLabel = true;
                field_group.addField(imageOverlayLink);

                field_groups.push(field_group);

                field_group = new FieldGroupModel('Videos');
                var youtubeId = new FieldModel('YouTube Video ID', this.vehicle.vehicleImages.youTubeId, 'text', 'youTubeId', true, false, null)
                if (this.editActive)
                {
                    youtubeId.hasButton = true;
                    youtubeId.buttonText = "Check link";
                    youtubeId.buttonIonIcon = "open-outline";
                    youtubeId.buttonName = "button-youtube";
                    youtubeId.launchOnLoad = true;
                }
                else if (this.vehicle.vehicleImages.youTubeId)
                {
                    //show a link
                    var url = "https://youtu.be/" + encodeURIComponent(this.vehicle.vehicleImages.youTubeId);
                    youtubeId = new FieldModel("YouTube Video", url, "url", "youTubeId", false, false, null, '', '', true);
                }
                field_group.addField(youtubeId);

                field_groups.push(field_group);

                break;
            case 'custom':
                field_group = new FieldGroupModel("Custom Fields");
                field_group.singleColumn = true;
                for (let i = 1; i <= 12; i++)
                {
                    const customFieldDisplayModel = CustomFieldDisplayModel.fromDto(this.vehicle.vehicleCustomFields[`customField${i}_Object`]);
                    if (customFieldDisplayModel.visible)
                    {
                        const customFieldValue = this.vehicle.vehicleCustomFields[`customField${i}`];
                        field_group.addField(new FieldModel(customFieldDisplayModel.name, customFieldValue, customFieldDisplayModel.fieldModelType, `customField${i}`, true, false, customFieldDisplayModel.dropdownOptions));
                    }
                }
                field_groups.push(field_group);
                break;
            case 'highlights':
                field_group = new FieldGroupModel('Highlight 1');
                field_group.addField(new FieldModel('Highlight 1', this.vehicle.vehicleExport.highlight1.highlightShow, 'toggle', 'highlightShow', false, false, null));
                field_group.addField(new FieldModel('Use AI generated', this.vehicle.vehicleExport.highlight1.highlightIsAi, 'toggle', 'highlightIsAi', false, false));
                field_group.addField(new FieldModel('Title', this.vehicle.vehicleExport.highlight1.highlightTitle, 'text', 'highlightTitle', false, false));
                field_group.addField(new FieldModel('Description', this.vehicle.vehicleExport.highlight1.highlightDescription, 'longtext', 'highlightDescription', false, false));        
                field_group.addField(new FieldModel('Icon', this.vehicle.vehicleExport.highlight1.highlightLogo, 'text', 'highlightLogo', false, false));
                field_groups.push(field_group);

                field_group = new FieldGroupModel('Highlight 2');
                field_group.addField(new FieldModel('Highlight 2', this.vehicle.vehicleExport.highlight2.highlightShow, 'toggle', 'highlightShow', false, false, null));
                field_group.addField(new FieldModel('Use AI generated', this.vehicle.vehicleExport.highlight2.highlightIsAi, 'toggle', 'highlightIsAi', false, false));
                field_group.addField(new FieldModel('Title', this.vehicle.vehicleExport.highlight2.highlightTitle, 'text', 'highlightTitle', false, false));
                field_group.addField(new FieldModel('Description', this.vehicle.vehicleExport.highlight2.highlightDescription, 'longtext', 'highlightDescription', false, false));
                field_group.addField(new FieldModel('Icon', this.vehicle.vehicleExport.highlight2.highlightLogo, 'text', 'highlightLogo', false, false));
                field_groups.push(field_group);

                field_group = new FieldGroupModel('Highlight 3');
                field_group.addField(new FieldModel('Highlight 3', this.vehicle.vehicleExport.highlight3.highlightShow, 'toggle', 'highlightShow', false, false, null));
                field_group.addField(new FieldModel('Use AI generated', this.vehicle.vehicleExport.highlight3.highlightIsAi, 'toggle', 'highlightIsAi', false, false));
                field_group.addField(new FieldModel('Title', this.vehicle.vehicleExport.highlight3.highlightTitle, 'text', 'highlightTitle', false, false));
                field_group.addField(new FieldModel('Description', this.vehicle.vehicleExport.highlight3.highlightDescription, 'longtext', 'highlightDescription', false, false));
                field_group.addField(new FieldModel('Icon', this.vehicle.vehicleExport.highlight3.highlightLogo, 'text', 'highlightLogo', false, false));
                field_groups.push(field_group);
                
                break;
        }
        return field_groups;
    }

	saveVehicle(websiteId: string, vehicleId: string, vehicle: WebsiteVehicleModel): Observable<WebsiteVehicleModel>
	{
        return this.httpClient.put<WebsiteVehicleModel>(Constants.ApiUrl + 'Websites/' + websiteId + '/Vehicles/' + vehicleId, vehicle);
	}

    scheduleVehiclesExport(websiteId: string): Observable<ExportSpecModel>
    {
        return this.httpClient.post<ExportSpecModel>(Constants.ApiUrl + 'Websites/' + websiteId + '/Vehicles/ScheduleExport', null);
    }
    // #endregion

    // #endregion


    // #region Subjects
    public blogMessage = new Subject<WebsiteBlogEntryModel>();
    public testimonialMessage = new Subject<WebsiteTestimonialModel>();
    public productMessage = new Subject<WebsiteProductModel>();
    public vehicleMessage = new Subject<WebsiteVehicleSummaryModel>();
    // #endregion


    // #region Utility functions
    getPossibleBusinessHours(includeClosed: boolean = false): string[]
    {
        //create an array of every 15 minutes from 12:00 AM to 11:45 PM
        var times = [];
        if (includeClosed)
        {
            times.push({ id: "Closed", name: "Closed" });
            times.push({ id: "By Appointment", name: "By Appointment" });
        }
        for (var i = 0; i < 24; i++)
        {
            for (var j = 0; j < 60; j += 15)
            {
                var hour = i % 12;
                hour = hour ? hour : 12; // the hour '0' should be '12'
                var minute = j < 10 ? "0" + j : j;
                var period = i < 12 ? "AM" : "PM";
                var time = hour + ":" + minute + " " + period;
                times.push({ id: time, name: time });
            }
        }

        return times;
    }

    public base64toBlob(byteString): Blob
    {
        return StringHelper.base64toBlob(byteString); //convert base64/URLEncoded data component to raw binary data held in a string
    }

    public numberWithCommas(x: any): string
    {
        return StringHelper.numberWithCommas(x);
    }
    
	async saveRecentVehicle(vehicle: WebsiteVehicleSummaryModel)
	{
		// console.debug("saveRecentVehicle", vehicle);
		var val = await this.storageService.get(Constants.IonicStorage_WebsiteVehiclesRecent);

		var recentVehicles = [];
		if (val)
		{
			recentVehicles = JSON.parse(val);
			if (!Array.isArray(recentVehicles))
			{
				recentVehicles = [];
			}
			else
			{
				recentVehicles = recentVehicles.filter(item => item.stockNo !== vehicle.stockNo); //remove vehicle if it is there already
			}
		}

		recentVehicles.unshift(vehicle);

		if (recentVehicles.length > 20)
		{
			recentVehicles = recentVehicles.slice(0, 20);
		}

		await this.storageService.set(Constants.IonicStorage_RecentVehicles, JSON.stringify(recentVehicles));
	}
    // #endregion

    public handleFieldChangedForTabs(ev:any, fieldGroupTabs:TabModel[])
    {
        // console.log("handleFieldChangedForTabs", ev);

        //see if this field has children
        var changedField: FieldModel = ev.detail.field;
        if (changedField.hasChildren)
        {
            switch (changedField.type)
            {
                case "autocomplete":
                    if (ev.autocomplete)
                    {
                        this.checkAddressChildren(changedField, <AddressLookupModel>ev.autocomplete, fieldGroupTabs);
                    }
                    break;
            }
        }
    }

    public checkAddressChildren(changedField: FieldModel, autocomplete: AddressLookupModel, fieldGroupTabs: TabModel[], togglePostal=false)
	{

		var prefix = changedField.detailClass;
		// console.log("checkAddressChildren", changedField);
		for (let field_group_tab of fieldGroupTabs)
		{
			for (let field_group of field_group_tab.fieldGroups)
			{
				for (let field_test of field_group.fields)
				{
					if (field_test.parent == changedField.id)
					{
						// console.log("found a child, " + field_test.id);

						var fieldWithoutPrefix = field_test.id.replace(prefix, '');
						field_test.dirty = true;

						// console.log(fieldWithoutPrefix);
						switch (fieldWithoutPrefix)
						{
							case "2":
								field_test.value = autocomplete.locality;
								break;
                            case "City":
                                field_test.value = autocomplete.city;
                                break;
                            case "Google":
                                function encodePart(part) {
                                    return part ? part.split(' ').join('+') : '';
                                }

                                const streetNumber = encodePart(autocomplete.street_number);
                                const street = encodePart(autocomplete.street);
                                const locality = encodePart(autocomplete.locality);
                                const city = encodePart(autocomplete.city);
                                const postalCode = encodePart(autocomplete.postcode);

                                const addressParts = [
                                    [streetNumber].filter(Boolean).join('+'),
                                    [street, locality, city].filter(Boolean).join(',+'),
                                    postalCode
                                ].filter(Boolean);

                                const address = addressParts.join('+') + ',+New+Zealand';
                                field_test.value = address;
                                break;
                            case "Latitude":
                                field_test.value = autocomplete.lat;
                                break;
                            case "Longitude":
                                field_test.value = autocomplete.lon;
                                break;
							// case "Address3":
							//     field_test.value = autocomplete.region;
							//     break;
							// case "PostCode":
							// 	field_test.value = autocomplete.postcode;
							// 	break;
							// case "CityId":
							// 	field_test.value = autocomplete.cityObject.id;
							// 	// field_test.editValue = '00000000-0000-0000-0000-00000000000';

							// 	field_test.editValue = autocomplete.cityObject.id;
							// 	field_test.detailObject = autocomplete.cityObject;
						}

					}
				}
			}
		}
	}



}
